import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Trash2, Plus, FileText, AlertTriangle, Loader2, FileDown, PlusIcon, MinusIcon, Undo, Mail, Download, AlertCircle, Settings, Eye } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  Timestamp, 
  doc, 
  deleteDoc, 
  updateDoc, 
  getDoc, 
  limit,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase'; // Import db from firebase.js
import { useTheme } from './ui/theme-provider'; // Add the theme provider import
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import { LoadingSpinner } from './ui/loading-spinner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { SimpleCalendar } from "./ui/simple-calendar";
import { Alert, AlertTitle, AlertDescription } from "./ui/alert";
import { dailyLogService } from "../services/dailyLogService";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { invoiceNumberService } from '../services/invoiceNumberService';
import { practiceRateCache } from "../utils/practiceRateCache";
import { formatInvoiceEmail } from "../services/invoiceService";
import { generateInvoicePDF, generateInvoicePDFBlob } from '../services/InvoicePDFGenerator';
import PracticeSettings from './PracticeSettings'; // Import PracticeSettings
import { usePracticeData } from '../contexts/PracticeDataContext'; // Add this import
import { practiceService } from '../services/practiceService'; // Add new import
import { practiceDataUtil } from '../helpers/practiceDataUtil';
import DailyInvoicePreview from './DailyInvoicePreview';
import { useInvoiceDraft } from '../hooks/useInvoiceDraft';

// Only autosave when the user has entered meaningful data.
// Prevents a phantom draft being created from the reset/blank form
// after a successful save (since resetForm() leaves `date` set to today).
const dailyDraftHasContent = (d) =>
  parseFloat(d.hours) > 0 ||
  !!(d.description?.trim()) ||
  !!(d.patients?.some(p => p.count && parseInt(p.count) > 0));

const DailyRecordLog = ({ user, selectedPractices: initialSelectedPractices, profileData }) => {
  // State variables
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false); // Added missing loading state loading state
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [date, setDate] = useState(new Date());
  const [hours, setHours] = useState('');
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [rateValue, setRateValue] = useState('');
  const [rateType, setRateType] = useState('percentage');
  const [description, setDescription] = useState('');
  const [filterPractice, setFilterPractice] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [selectedPracticeForInvoice, setSelectedPracticeForInvoice] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  // State for patients (for percentage rate type)
  const [patients, setPatients] = useState([{ count: '', rate: '', description: '' }]);
  const [saveConfirmation, setSaveConfirmation] = useState(null);
  
  // ── Daily record autosave draft ────────────────────────────────────────────
  const dailyDraftKey = user?.uid ? `invoice-daily-${user.uid}` : null;
  const { savedDraft: dailyDraft, clearDraft: clearDailyDraft, dismissDraft: dismissDailyDraft, acknowledgeDraft: acknowledgeDailyDraft } =
    useInvoiceDraft(dailyDraftKey, { date, hours, rateType, rateValue, description, patients, selectedPractice }, 2000, dailyDraftHasContent);

  const handleRestoreDailyDraft = () => {
    if (!dailyDraft) return;
    if (dailyDraft.date) setDate(dailyDraft.date);
    if (dailyDraft.hours !== undefined) setHours(dailyDraft.hours);
    if (dailyDraft.rateType) setRateType(dailyDraft.rateType);
    if (dailyDraft.rateValue !== undefined) setRateValue(dailyDraft.rateValue);
    if (dailyDraft.description !== undefined) setDescription(dailyDraft.description);
    if (dailyDraft.patients) setPatients(dailyDraft.patients);
    if (dailyDraft.selectedPractice) setSelectedPractice(dailyDraft.selectedPractice);
    acknowledgeDailyDraft();
  };
  // ────────────────────────────────────────────────────────────────────────
  
  // Get theme from context
  const { theme } = useTheme();
  
  const { toast } = useToast();
  // db is now imported from firebase.js

  // Add states for archive viewing
  const [showArchivedRecords, setShowArchivedRecords] = useState(false);
  const [archivePeriod, setArchivePeriod] = useState('30');  // '30' or '60' days
  const [archivedLogs, setArchivedLogs] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);

  // Add states for unarchive functionality
  const [showUnarchiveDialog, setShowUnarchiveDialog] = useState(false);
  const [logToUnarchive, setLogToUnarchive] = useState(null);
  const [unarchiveLoading, setUnarchiveLoading] = useState(false);
  
  // Add states for PDF generation and email
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedLogsForPdf, setSelectedLogsForPdf] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState(null);
  // Add new state for PDF filename tracking to replace window global
  const [generatedPdfFilename, setGeneratedPdfFilename] = useState(null);

  // Add a flag to track if PDF generation has been triggered to prevent duplicates
  const [pdfGenerationInProgress, setPdfGenerationInProgress] = useState(false);

  // Add state for showing practice settings dialog
  const [showPracticeSettings, setShowPracticeSettings] = useState(false);

  // Get practice data from context instead of relying solely on props
  const { 
    selectedPractices: contextPractices, 
    loadUserSelectedPractices,
    hasUpdates
  } = usePracticeData();
  
  // Use practice data from context first, fall back to props
  const [selectedPractices, setSelectedPractices] = useState([]);

  // On mount: trigger a Firestore read via context so contextPractices gets populated.
  // loadUserSelectedPractices reads users/{uid}.selectedPractices (codes) from Firestore
  // and sets context state — the sync effect below then mirrors it into local state.
  useEffect(() => {
    if (user?.uid) {
      loadUserSelectedPractices(user.uid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // Sync local selectedPractices whenever the context value changes.
  // Also handles the auto-select for the dropdown using functional setState
  // to avoid stale-closure overwrites (e.g. after a draft restore).
  useEffect(() => {
    if (!contextPractices?.length) return;
    setSelectedPractices(contextPractices);
    setSelectedPractice(current => {
      if (current) return current; // already set (e.g. restored from draft)
      const lastSelected = localStorage.getItem('selectedPractice');
      return (lastSelected && contextPractices.some(p => p.prac_code === lastSelected))
        ? lastSelected
        : contextPractices[0].prac_code;
    });
  }, [contextPractices]);

  // Effect to reset patient entries when rate type changest patient entries when rate type changes
  useEffect(() => {
    if (rateType === 'percentage' || rateType === 'per_patient') {
      // Initialize with one empty patient entry if changing to percentage or per_patient
      if (patients.length === 0) {
        setPatients([{ count: '', rate: '', description: '' }]);
      }
    }
  }, [rateType]);

  // Load saved logs when component mounts or filters or archive toggle changes
  useEffect(() => {
    if (user?.uid) {
      loadLogs();
    }
  }, [user, filterPractice, filterMonth, showArchivedRecords, archivePeriod]);

  // Add new effect to handle setting cached rate when practice changes
  useEffect(() => {
    if (selectedPractice && user?.uid) {
      const cachedRate = practiceRateCache.get(selectedPractice, user.uid);
      if (cachedRate) {
        console.log(`Found cached rate for ${selectedPractice}: ${cachedRate.rateType} @ ${cachedRate.rateValue}`);
        setRateType(cachedRate.rateType);
        setRateValue(cachedRate.rateValue);
      }
    }
  }, [selectedPractice, user?.uid]);

  // Auto-save rate to cache when rate type/value changes (same behaviour as Monthly)
  useEffect(() => {
    if (selectedPractice && user?.uid && rateValue && String(rateValue).trim() !== '') {
      practiceRateCache.set(selectedPractice, user.uid, rateType, rateValue);
      console.log(`💾 Daily: Cached rate for practice ${selectedPractice}:`, { rateType, rateValue });
    }
  }, [selectedPractice, user?.uid, rateType, rateValue]);

  // Enhanced filter handler to actually apply the practice filter
  const loadLogs = async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      console.log('📅 Loading logs with filters:', {
        month: filterMonth, 
        practice: filterPractice,
        showArchived: showArchivedRecords,
        viewState: showArchivedRecords ? 'ARCHIVE' : 'ACTIVE'
      });
      
      // Force-clear any cached data to ensure fresh results
      console.log('📅 Clearing cached results before query');
      
      // Prepare query options - start with empty options
      const options = {};
      
      // Only apply date filter if a specific month is selected (not "all")
      if (filterMonth !== 'all') {
        // Get start and end date for the selected month
        const [year, month] = filterMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0, 23, 59, 59);
        
        // Add defensive check to ensure dates are valid
        if (startDate instanceof Date && !isNaN(startDate.getTime()) && 
            endDate instanceof Date && !isNaN(endDate.getTime())) {
          console.log('📅 Date Range:', { 
            startDate: startDate.toISOString(), 
            endDate: endDate.toISOString(),
            month, year
          });
          
          options.startDate = startDate;
          options.endDate = endDate;
        } else {
          console.error('📅 Invalid date range created:', { year, month });
        }
      } else {
        console.log('📅 No date filter applied - showing all records');
      }
      
      // Add practice filter if selected
      if (filterPractice !== 'all') {
        console.log('🏥 Filtering by practice:', filterPractice);
        options.practiceId = filterPractice;
      }
      
      // Decide which collection to query based on archive toggle
      if (showArchivedRecords) {
        // Calculate date threshold based on selected period
        const periodDays = parseInt(archivePeriod);
        const thresholdDate = new Date();
        thresholdDate.setDate(thresholdDate.getDate() - periodDays);
        
        console.log('📦 Loading from ARCHIVE with threshold:', thresholdDate.toISOString());
        
        // FIXED: Don't pass minDate to avoid filtering issues
        const result = await dailyLogService.getArchivedLogEntries(
          user.uid, 
          { ...options }
        );
        console.log(`📦 Archive query returned ${result.logs.length} logs`); 
        
        // FIXED: Make sure we properly update both logs arrays 
        // When viewing archived logs, the archived logs should go in the main logs array
        setLogs(result.logs);
        setArchivedLogs([]);
      } else {
        // Load from active collection with non-invoiced filter
        options.invoiced = false;
        console.log('📝 Loading from ACTIVE logs with options:', options);
        const result = await dailyLogService.getLogEntries(user.uid, options);
        console.log(`📝 Active query returned ${result.logs.length} logs`);
        
        // Add detailed log info for debugging
        if (result.logs.length > 0) {
          console.log('📝 First log date:', result.logs[0].date);
          console.log('📝 Date type:', typeof result.logs[0].date);
          console.log('📝 Is Date object?', result.logs[0].date instanceof Date);
        } else {
          console.log('⚠️ No active logs found with current filters');
        }
        
        // Validate dates in received logs
        if (result.logs.length > 0) {
          console.log('📅 Validating dates in logs...');
          result.logs = result.logs.map(log => {
            // If date is invalid, try to fix it or mark it clearly
            if (log.date && !(log.date instanceof Date && !isNaN(log.date.getTime()))) {
              console.warn('📅 Invalid date detected in log, attempting to fix:', log);
              // Try to repair the date if possible
              if (typeof log.date === 'string') {
                try {
                  log.date = new Date(log.date);
                } catch (e) {
                  console.error('📅 Could not parse date string:', log.date);
                  log.date = null;
                }
              } else if (log.date && typeof log.date === 'object' && log.date._seconds) {
                // Handle Firestore timestamp objects
                try {
                  log.date = new Date(log.date._seconds * 1000);
                } catch (e) {
                  console.error('📅 Could not convert timestamp:', log.date);
                  log.date = null;
                }
              } else {
                log.date = null;
              }
            }
            return log;
          });
        }
        
        setLogs(result.logs);
        setArchivedLogs([]);
      }
      
      // Add clear log when loading completes
      console.log(`📅 Loaded ${showArchivedRecords ? 'ARCHIVED' : 'ACTIVE'} logs successfully:`, {
        count: showArchivedRecords ? archivedLogs.length : logs.length,
        viewState: showArchivedRecords ? 'ARCHIVE' : 'ACTIVE'
      });
      
    } catch (error) {
      console.error("❌ Error loading logs:", error);
      toast({
        title: "Error",
        description: "Failed to load your records",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Add patient to the list
  const handleAddPatient = () => {
    setPatients([...patients, { count: '', rate: '', description: '' }]);
  };

  // Remove patient from the list
  const handleRemovePatient = (indexToRemove) => {
    if (patients.length > 1) {
      setPatients(patients.filter((_, index) => index !== indexToRemove));
    }
  };

  // Update patient data
  const handlePatientChange = (index, field, value) => {
    const updatedPatients = [...patients];
    updatedPatients[index][field] = value;
    setPatients(updatedPatients);
  };

  // Save a new daily log
  const handleSave = async () => {
    if (!selectedPractice) {
      toast({
        title: "Error",
        description: "Please select a practice",
        variant: "destructive",
      });
      return;
    }

    if (!date) {
      toast({
        title: "Error",
        description: "Please select a date",
        variant: "destructive",
      });
      return;
    }

    // Validate based on rate type
    if (rateType === 'hourly' && (!hours || isNaN(parseFloat(hours)))) {
      toast({
        title: "Error",
        description: "Please enter valid work hours",
        variant: "destructive",
      });
      return;
    }

    if (rateType === 'percentage' || rateType === 'per_patient') {
      // Check if at least one patient has valid count and rate
      const hasValidPatient = patients.some(
        p => p.count && !isNaN(parseInt(p.count)) && p.rate && !isNaN(parseFloat(p.rate))
      );
      
      if (!hasValidPatient) {
        toast({
          title: "Error",
          description: "Please enter at least one valid patient with count and rate",
          variant: "destructive",
        });
        return;
      }
    }

    if (rateType === 'fixed' && !rateValue) {
      toast({
        title: "Error",
        description: "Please enter a rate value",
        variant: "destructive",
      });
      return;
    }

    // Find the practice details
    const practice = selectedPractices.find(p => p.prac_code === selectedPractice);
    
    if (!practice) {
      toast({
        title: "Error",
        description: "Selected practice not found",
        variant: "destructive",
      });
      return;
    }

    setSaveInProgress(true);
    setSaveConfirmation(null);
    
    try {
      // Create log object based on rate typeect based on rate type
      const logData = {
        userId: user.uid,
        practiceId: practice.prac_code,
        practiceName: practice.prac_name,
        date,
        rateType,
        rateValue: rateValue ? parseFloat(rateValue) : null,
        description
      };

      // Add rate type specific data
      if (rateType === 'hourly' || rateType === 'fixed') {
        logData.hours = parseFloat(hours) || 0;
      }

      if (rateType === 'percentage' || rateType === 'per_patient') {
        // Filter out invalid patients
        const validPatients = patients.filter(
          p => p.count && !isNaN(parseInt(p.count)) && p.rate && !isNaN(parseFloat(p.rate))
        ).map(p => ({
          count: parseInt(p.count),
          rate: parseFloat(p.rate),
          description: p.description || ''
        }));
        
        logData.patients = validPatients;
        
        // Add total patient count to the log for easier display
        logData.patientCount = validPatients.reduce((sum, p) => sum + p.count, 0);
        
        // Store rate value in cache for this practice
        if (selectedPractice && user?.uid && rateType && rateValue) {
          practiceRateCache.set(selectedPractice, user.uid, rateType, rateValue);
        }
        
        // Add average rate (weighted by patient count) for easier calculation
        const totalValue = validPatients.reduce((sum, p) => sum + (p.count * p.rate), 0);
        if (logData.patientCount > 0) {
          logData.rate = totalValue / logData.patientCount;
        }
      }

      // Save to Firestore using service
      await dailyLogService.saveLogEntry(user.uid, logData);
      clearDailyDraft();
      
      // Reset formm
      resetForm();
      
      // IMPORTANT: Auto-switch filterMonth to match the month of the record we just saved the record we just saved
      // This ensures newly saved records are immediately visibleediately visible
      const savedMonth = format(date, 'yyyy-MM');
      const currentFilterMonth = filterMonth;
      
      console.log('Saved record month:', savedMonth);
      console.log('Current filter month:', currentFilterMonth);
      
      if (savedMonth !== currentFilterMonth) {
        console.log(`Automatically switching filter from ${currentFilterMonth} to ${savedMonth} to show new record`);
        setFilterMonth(savedMonth);
      } else {
        console.log('Saved record matches current month filter, reloading logs');
      }
      
      // Reload logs with a small delay to ensure Firestore has indexed the new recordh a small delay to ensure Firestore has indexed the new record
      setTimeout(() => {
        loadLogs();
      }, 500);
      
      // Show confirmation message that will auto-dismissmessage that will auto-dismiss
      setSaveConfirmation({
        message: "Record saved successfully",
        type: "success"
      });
      
      // Set a timer to auto-dismiss the confirmationauto-dismiss the confirmation
      setTimeout(() => {
        setSaveConfirmation(null);
      }, 3000);
      
      toast({
        title: "Success",
        description: "Daily record saved successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Error saving log:", error);
      setSaveConfirmation({
        message: "Failed to save record",
        type: "error"
      });
      
      toast({
        title: "Error",
        description: "Failed to save your daily record",
        variant: "destructive",
      });
      
      // Auto-dismiss error message after longer periodror message after longer period
      setTimeout(() => {
        setSaveConfirmation(null);
      }, 5000);
    } finally {
      setSaveInProgress(false);
    }
  };

  // Reset form fields
  const resetForm = () => {
    setHours('');
    setDescription('');
    setPatients([{ count: '', rate: '', description: '' }]);
    
    // After saving a record, reload the cached rate data for the selected practice
    if (selectedPractice && user?.uid) {
      const cachedRate = practiceRateCache.get(selectedPractice, user.uid);
      if (cachedRate) {
        console.log(`Reloading cached rate for ${selectedPractice}: ${cachedRate.rateType} @ ${cachedRate.rateValue}`);
        setRateType(cachedRate.rateType);
        setRateValue(cachedRate.rateValue);
      }
    }
  };

  // Delete a log entry - SIMPLIFIED to use the guaranteed correct document ID
  const handleDelete = (logId) => {
    console.log(`🗑️ Delete called with log ID: ${logId}`);
    // Find the log in our state
    const log = logs.find(log => log.id === logId);
    if (log) {
      confirmDeleteLog(log);
    } else {
      console.error(`🗑️ Log not found with ID: ${logId}`);
    }
  };

  // Confirm deletion dialog
  const confirmDeleteLog = (log) => {
    console.log(`🗑️ Confirming deletion of log: `, { id: log.id, practiceName: log.practiceName });
    setLogToDelete(log);
    setShowDeleteDialog(true);
  };

  // Handle the actual deletion - SIMPLIFIED to use document ID
  const handleDeleteLog = async () => {
    if (!logToDelete) return;

    try {
      setLoading(true);
      
      // Log the ID we're deleting for debugging
      console.log(`🗑️ Deleting log with ID: ${logToDelete.id}`);
      
      // Call the simplified delete method with the document ID
      const result = await dailyLogService.deleteLogEntry(logToDelete.id);
      
      if (result.success) {
        // Update UI by removing the deleted log
        setLogs(logs.filter(log => log.id !== logToDelete.id));
        
        toast({
          title: "Success",
          description: "Record deleted successfully",
          variant: "success",
        });
        
        // Force a reload of logs after deletion
        setTimeout(() => {
          console.log('🔄 Reloading logs after deletion');
          loadLogs();
        }, 1500);
      } else {
        console.error(`🗑️ Deletion failed: ${result.error}`);
        toast({
          title: "Error",
          description: `Failed to delete record: ${result.error}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("🗑️ Error in delete process:", error);
      toast({
        title: "Error",
        description: `Failed to delete record: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
      setLogToDelete(null);
    }
  };

  // Improve toggle selection function to update both state variables simultaneously with explicit booleans
  const toggleLogSelection = (logId) => {
    // Update both selectedLogs and selectedLogsForPdf with the same changes
    const updateSelection = (prevLogs) => {
      return prevLogs.map(log => {
        if (log.id === logId) {
          // Use explicit boolean for selection state
          const newSelectedState = log.selected === true ? false : true;
          return { ...log, selected: newSelectedState };
        }
        return log;
      });
    };
    
    // Apply the same update to both states
    setSelectedLogs(updateSelection);
    setSelectedLogsForPdf(updateSelection);
    
    console.log(`Toggled selection for log ${logId}`);
  };

  // Modified function to handle opening the invoice dialog
  const handleOpenInvoiceDialog = (practiceId, practiceName) => {
    // Get all logs for this practice and ensure they have valid dates
    const practiceLogs = logs
      .filter(log => log.practiceId === practiceId)
      .map(log => {
        // Ensure the date is valid
        if (log.date && !(log.date instanceof Date && !isNaN(log.date.getTime()))) {
          console.warn('Invalid date detected when opening invoice dialog:', log);
          // Create a shallow copy with fixed date
          return {
            ...log,
            date: null,
            formattedDate: 'Unknown date'
          };
        }
        return log;
      });
    
    // Set all logs as pre-selected by default with explicit boolean
    const updatedLogs = practiceLogs.map(log => ({
      ...log, 
      selected: true // Use explicit true value
    }));
    
    // Set both states simultaneously with the same data
    setSelectedLogs(updatedLogs);
    setSelectedLogsForPdf(updatedLogs);
    
    setSelectedPracticeForInvoice({
      id: practiceId,
      name: practiceName
    });
    setShowInvoiceDialog(true);
    
    // Reset any PDF-related flags when opening dialog
    setGeneratedPdfFilename(null); // Reset the PDF filename state
    setPdfGenerationInProgress(false);
    setPdfLoading(false);
  };

  // Update handleGenerateInvoice to force archive regardless of restoration status
  const handleGenerateInvoice = async () => {
    if (!selectedPracticeForInvoice || selectedLogs.length === 0) {
      toast({
        title: "Error",
        description: "Please select records to include in the invoice",
        variant: "destructive",
      });
      return;
    }
    
    setGenerateLoading(true);
    try {
      // Get all selected logs
      const logsToInvoice = selectedLogs.filter(log => log.selected !== false);
      if (logsToInvoice.length === 0) {
        toast({
          title: "Error",
          description: "Please select at least one record to include",
          variant: "destructive",
        });
        return;
      }
      
      // Get practice details
      const practiceDetails = selectedPractices.find(p => p.prac_code === selectedPracticeForInvoice.id);
      if (!practiceDetails) {
        throw new Error('Practice details not found');
      }
      
      // Get next invoice numbere number
      let invoiceNumber;
      try {
        invoiceNumber = await invoiceNumberService.generateInvoiceNumber(
          user.uid, 
          profileData,
          { forSaving: true, persistToDatabase: true }
        );
        setCurrentInvoiceNumber(invoiceNumber);
      } catch (error) {
        console.error('Error generating invoice number:', error);
        throw new Error('Failed to generate invoice number');
      }
      
      // Format the logs into the same structure as MonthlyInvoiceo the same structure as MonthlyInvoice
      // Group by date first
      const logsByDate = logsToInvoice.reduce((acc, log) => {
        const dateKey = format(log.date, 'yyyy-MM-dd');
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(log);
        return acc;
      }, {});
      
      // Convert to summary format
      const summaryData = Object.entries(logsByDate).map(([dateKey, dateLogs]) => {
        const date = dateLogs[0].date;
        const formattedDate = format(date, 'dd MMM yyyy');
        
        // Handle different rate types
        if (dateLogs[0].rateType === 'percentage') {
          // Collect all patients across all logs for this date across all logs for this date
          const allPatients = [];
          let totalPatientCount = 0;
          let totalAmount = 0;
          dateLogs.forEach(log => {
            if (log.patients && log.patients.length > 0) {
              log.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                const subtotal = count * rate;
                const percentageRate = parseFloat(log.rateValue) || 0;
                const amount = subtotal * (percentageRate / 100);
                allPatients.push({ 
                  count,  
                  rate, 
                  description: patient.description || '',
                  rateType: 'percentage',
                  rateValue: percentageRate
                });
                totalPatientCount += count;
                totalAmount += amount;
              });
            }
          });
        } else if (dateLogs[0].rateType === 'per_patient') {
          // Collect all patients across all logs for this date
          const allPatients = [];
          let totalPatientCount = 0;
          let totalAmount = 0;
          dateLogs.forEach(log => {
            if (log.patients && log.patients.length > 0) {
              log.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                const amount = count * rate; // No percentage calculation for per_patient
                allPatients.push({ 
                  count,  
                  rate, 
                  description: patient.description || '',
                  rateType: 'per_patient'
                });
                totalPatientCount += count;
                totalAmount += amount;
              });
            }
          });
          
          return {
            date,
            formattedDate,
            amount: totalAmount,
            patients: allPatients,
            totalPatients: totalPatientCount,
            rateType: 'per_patient',
            description: allPatients.map(p => 
              `${p.count} × £${p.rate.toFixed(2)}${p.description ? ` (${p.description})` : ''}`
            ).join(', ')
          };
        } else {
          // For hourly or fixed rates
          const totalHours = dateLogs.reduce((sum, log) => sum + (parseFloat(log.hours) || 0), 0);
          const rateValue = parseFloat(dateLogs[0].rateValue) || 0;
          const amount = dateLogs[0].rateType === 'hourly' ? totalHours * rateValue : rateValue;
          
          return {
            date,
            formattedDate,
            amount,
            hours: totalHours,
            rateType: dateLogs[0].rateType,
            rateValue,
            description: dateLogs[0].description || ''
          };
        }
      });
      
      // Calculate total
      const totalAmount = summaryData.reduce((sum, item) => sum + item.amount, 0);
      
      // Create invoice data matching MonthlyInvoice structurea matching MonthlyInvoice structure
      const invoiceData = {
        userId: user.uid,
        practiceId: selectedPracticeForInvoice.id,
        practiceDetails,
        invoiceNumber,
        totalAmount,
        createdAt: new Date(),
        rateType: logsToInvoice[0].rateType, // Using the first log's rate type as reference
        rateValue: logsToInvoice[0].rateValue,
        entriesCount: logsToInvoice.length,
        summaryData
      };
      
      // Save invoice to Firestore
      const invoiceRef = collection(db, 'invoices');
      const invoiceDocRef = await addDoc(invoiceRef, invoiceData);
      console.log('Invoice saved with ID:', invoiceDocRef.id);
      
      // Move logs to archive collection and delete from daily logs
      const logIds = logsToInvoice.map(log => log.id);
      console.log(`Archiving ${logIds.length} logs with invoice number: ${invoiceNumber}`);
      
      // Explicitly wait for the archive operation to complete and pass option to archive restored logs
      const archiveResult = await dailyLogService.archiveAndDeleteLogs(
        logIds, 
        invoiceNumber, 
        { ignoreRestorationStatus: true }
      );
      console.log('Archive operation completed:', archiveResult);
      
      toast({
        title: "Success",
        description: `Invoice ${invoiceNumber} generated successfully`,
        variant: "success",
      });
      
      // Reload logs to update the list update the list
      await loadLogs();
      setShowInvoiceDialog(false);
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast({
        title: "Error",
        description: "Failed to generate invoice: " + error.message,
        variant: "destructive",
      });
    } finally {
      setGenerateLoading(false);
    }
  };

  // Fix PDF generation to prevent duplicate PDFs
  const handleGeneratePDF = async () => {
    if (!selectedLogsForPdf.length || !profileData) {
      toast({
        title: "Error",
        description: "Please select records for PDF generation",
        variant: "destructive",
      });
      return null;
    }
    
    // Prevent duplicate PDF generation
    if (pdfLoading || pdfGenerationInProgress) {
      console.log('PDF generation already in progress, preventing duplicate');
      return null;
    }
    setPdfLoading(true);
    setPdfGenerationInProgress(true);
    
    try {
      console.log('Starting PDF generation process...');
      
      // Get selected logs and filter out any explicitly deselected
      const logsForPdf = selectedLogsForPdf.filter(log => log.selected !== false);
      console.log(`Selected ${logsForPdf.length} logs for PDF`);
      
      if (logsForPdf.length === 0) {
        toast({
          title: "Error",
          description: "No records selected for PDF",
          variant: "destructive",
        });
        setPdfLoading(false);
        setPdfGenerationInProgress(false);
        return null;
      }
      
      // Get practice details using the new practice service
      const practiceId = logsForPdf[0].practiceId;
      console.log('Looking for practice with ID:', practiceId);
      
      // Look up practice details from all available sources
      const practice = await practiceService.getPracticeById(practiceId, selectedPractices, user?.uid);
      
      // Check if we got back a practice or not
      if (!practice) {
        console.error(`Practice details not found for ID: ${practiceId}`);
        
        // Show a more user-friendly error message with clear next steps
        toast({
          title: "Missing Practice Details",
          description: (
            <div className="space-y-2">
              <p>
                Practice <strong>{practiceId}</strong> is needed but not found in your selected practices.
              </p>
              <p>
                The practice selector will open now. Please add this practice to continue.
              </p>
            </div>
          ),
          variant: "destructive",
          duration: 8000, // Show for longer to give user time to read
        });
        
        // Wait for toast to be visible before opening dialog
        setTimeout(() => {
          // Automatically open practice settings dialog to help user add the practice
          setShowPracticeSettings(true);
        }, 2000);
        
        // Clear loading states
        setPdfLoading(false);
        setPdfGenerationInProgress(false);
        
        throw new Error(`Practice ${practiceId} not found. Please add this practice to your list.`);
      }
      
      console.log('Found practice for PDF:', practice);
      
      // Only generate invoice number if not already set
      let invoiceNumber = currentInvoiceNumber;
      if (!invoiceNumber) {
        toast({
          title: "Generating Invoice",
          description: "Creating invoice number...",
        });
        invoiceNumber = await invoiceNumberService.generateInvoiceNumber(
          user.uid,
          profileData,
          { forSaving: true, persistToDatabase: true }
        );
        if (!invoiceNumber) {
          throw new Error("Failed to generate invoice number");
        }
        setCurrentInvoiceNumber(invoiceNumber);
        console.log(`Generated fresh invoice number: ${invoiceNumber}`);
      } else {
        console.log(`Using existing invoice number: ${invoiceNumber}`);
      }

      toast({
        title: "Generating PDF",
        description: "Creating your invoice PDF...",
      });

      // Create a properly formatted data structure for the PDF generator
      const doc = new jsPDF();
      
      // Group logs by date for better formatting
      const logsByDate = logsForPdf.reduce((acc, log) => {
        // Add defensive check to ensure date is valid
        if (!log.date || !(log.date instanceof Date) || isNaN(log.date.getTime())) {
          console.error('Invalid date in log:', log);
          return acc;
        }
        
        const dateKey = format(log.date, 'yyyy-MM-dd');
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(log);
        return acc;
      }, {});
      
      // Format entries for PDF generation with exact field structure required
      const entries = Object.entries(logsByDate).map(([dateKey, dateLogs]) => {
        const date = dateLogs[0].date;
        const formattedDate = format(date, 'dd MMM yyyy');
        
        if (dateLogs[0].rateType === 'percentage') {
          // Process percentage-based logs
          const allPatients = [];
          let totalPatientCount = 0;
          let amount = 0;
          let descriptionParts = [];

          dateLogs.forEach(log => {
            if (log.patients && log.patients.length > 0) {
              log.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                const subtotal = count * rate;
                const percentageRate = parseFloat(log.rateValue) || 0;
                const itemAmount = subtotal * (percentageRate / 100);
                amount += itemAmount;
                const patientDesc = patient.description ? ` (${patient.description})` : '';
                // Add calculation details for each patient type, but match UI: no trailing = £amount, just the calculation
                descriptionParts.push(`${count} × £${rate.toFixed(2)} @ ${percentageRate}%${patientDesc}`);
                allPatients.push({
                  count,
                  rate,
                  subtotal,
                  total: itemAmount,
                  description: patient.description || '',
                  rateType: 'percentage',
                  rateValue: percentageRate
                });
                totalPatientCount += count;
              });
            }
          });

          // Join with comma and space, match UI style
          const description = descriptionParts.join(', ');

          // Patch: Always use this description for PDF, and ensure it is passed in the entry object
          return {
            date,
            formattedDate,
            amount,
            patients: allPatients,
            totalPatients: totalPatientCount,
            rateType: 'percentage',
            rateValue: parseFloat(dateLogs[0].rateValue) || 0,
            description // This is the full calculation details string for PDF and UI
          };
        } else if (dateLogs[0].rateType === 'per_patient') {
          // Process per_patient-based logs (similar to percentage but no percentage calculation)
          const allPatients = [];
          let totalPatientCount = 0;
          let amount = 0;
          let descriptionParts = [];

          dateLogs.forEach(log => {
            if (log.patients && log.patients.length > 0) {
              log.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                const itemAmount = count * rate; // No percentage calculation for per_patient
                amount += itemAmount;
                const patientDesc = patient.description ? ` (${patient.description})` : '';
                // Add calculation details for each patient type
                descriptionParts.push(`${count} × £${rate.toFixed(2)}${patientDesc}`);
                allPatients.push({
                  count,
                  rate,
                  subtotal: itemAmount,
                  total: itemAmount,
                  description: patient.description || '',
                  rateType: 'per_patient'
                });
                totalPatientCount += count;
              });
            }
          });

          // Join with comma and space, match UI style
          const description = descriptionParts.join(', ');

          return {
            date,
            formattedDate,
            amount,
            patients: allPatients,
            totalPatients: totalPatientCount,
            rateType: 'per_patient',
            description // This is the full calculation details string for PDF and UI
          };
        } else {
          // Process hourly or fixed rate logs
          const totalHours = dateLogs.reduce((sum, log) => sum + (parseFloat(log.hours) || 0), 0);
          const rateValue = parseFloat(dateLogs[0].rateValue) || 0;

          // Calculate amount based on rate type
          const amount = dateLogs[0].rateType === 'hourly'
            ? totalHours * rateValue
            : rateValue;

          // Base description
          let description = dateLogs[0].rateType === 'hourly'
            ? `${totalHours} hr${totalHours !== 1 ? 's' : ''} @ £${rateValue.toFixed(2)}/hr`
            : `Fixed rate: £${rateValue.toFixed(2)}`;

          // Append user-provided description if it exists across any log for the day
          const userDescriptions = dateLogs
            .map(log => log.description)
            .filter(desc => desc && desc.trim() !== '')
            .join('; '); // Join multiple descriptions if they exist

          if (userDescriptions) {
            description += ` (${userDescriptions})`;
          }

          return {
            date,
            formattedDate,
            amount,
            total: amount, // Add total field explicitly for PDF generator
            hours: totalHours,
            rate: rateValue, // Make sure this field is included
            rateType: dateLogs[0].rateType,
            rateValue: rateValue,
            description: description // Use the combined description
          };
        }
      });
      
      // Calculate total from entries with proper data validation
      const total = entries.reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);
      
      // Create properly formatted invoice data object that matches PDF generator expectations
      const invoiceData = {
        practice,
        profile: profileData,
        invoiceNumber,
        entries,
        total, // Make sure total is a number
        author: profileData?.name || 'Dental Hygienist',
        subject: `Invoice ${invoiceNumber}`,
        createdAt: new Date(),
        // Use rateType/rateValue from logs, fallback to state
        rateType: logsForPdf[0]?.rateType || rateType,
        rateValue: logsForPdf[0]?.rateValue ? parseFloat(logsForPdf[0].rateValue) : (rateValue ? parseFloat(rateValue) : null)
      };
      
      // Generate the PDF
      const result = await generateInvoicePDF(invoiceData);
      const { doc: pdfDoc, filename } = result;

      // Save the PDF - only save it here, nowhere else
      pdfDoc.save(filename);

      // Store filename for email reference
      setGeneratedPdfFilename(filename);
      
      toast({
        title: "PDF Created",
        description: "Your PDF has been downloaded successfully",
        variant: "success",
      });
      
      // Save to Firestore and archive logs - but DON'T generate a second PDF
      await saveInvoiceAndArchiveLogs(logsForPdf, practiceId, invoiceNumber, total, false); // Added 'false' parameter to skip PDF generation

      // At the end of successful PDF generation, show the modal:
      setConfirmationModalContent({
        title: 'Invoice PDF Generated',
        description: 'Your invoice PDF has been generated and is ready for download.'
      });
      setShowConfirmationModal(true);

      return filename;
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF: " + error.message,
        variant: "destructive",
      });
      return null;
    } finally {
      setPdfLoading(false);
      setPdfGenerationInProgress(false);
    }
  };

  // Modified function to save invoice and archive logs with proper practice lookup
  const saveInvoiceAndArchiveLogs = async (logs, practiceId, invoiceNumber, totalAmount, generatePdf = false) => {
    if (!logs || !logs.length || !practiceId || !invoiceNumber) {
      console.error('Missing required parameters for saveInvoiceAndArchiveLogs:', { 
        logsCount: logs?.length, 
        practiceId, 
        invoiceNumber 
      });
      return { success: false, error: 'Missing required parameters' };
    }

    try {
      console.log('💾 Starting saveInvoiceAndArchiveLogs', {
        logsCount: logs.length,
        practiceId,
        invoiceNumber,
        generatePdf
      });
      
      // Find practice details using the practice service
      console.log(`Looking up complete practice details for ID: ${practiceId}`);
      const practiceDetails = await practiceService.getPracticeById(practiceId, selectedPractices, user?.uid);
      
      // Check if we got back a practice or not
      if (!practiceDetails) {
        console.error(`Practice details not found for ID: ${practiceId}`);
        
        // Show a more user-friendly error message BEFORE opening the dialog
        toast({
          title: "Practice Not Found",
          description: `Practice "${practiceId}" is needed for this invoice but wasn't found. Please click "Add Practice" to select it.`,
          variant: "destructive",
          duration: 8000, // Show for longer to give user time to read
        });
        
        // Wait for toast to be visible before opening dialog
        setTimeout(() => {
          // Automatically open practice settings dialog to help user add the practice
          setShowPracticeSettings(true);
        }, 1500);
        
        throw new Error(`Practice details not found for ID: ${practiceId}. Please add this practice to your list.`);
      }
      
      console.log('Found practice details:', practiceDetails);
      
      // Extract log IDs FIRST
      const logIds = logs.map(log => log.id);
      console.log(`💾 Extracted ${logIds.length} log IDs for archive process`);
      if (!logIds.every(id => typeof id === 'string' && id.trim().length > 0)) {
        throw new Error('Invalid log IDs detected. Cannot archive logs.');
      }
      
      // Create properly formatted summaryData for invoices 
      // This must match the expected format for downstream processes
      const logsByDate = logs.reduce((acc, log) => {
        if (!log.date || !(log.date instanceof Date)) {
          console.warn('Invalid date in log, skipping for summary:', log);
          return acc;
        }
        
        const dateKey = format(log.date, 'yyyy-MM-dd');
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(log);
        return acc;
      }, {});
      
      // Format summary data exactly as expected by downstream processes
      const summaryData = Object.entries(logsByDate).map(([dateKey, dateLogs]) => {
        const date = dateLogs[0].date;
        const formattedDate = format(date, 'dd MMM yyyy');
        
        if (dateLogs[0].rateType === 'percentage') {
          // Combine all patients for this date
          const allPatients = [];
          let totalPatientCount = 0;
          let amount = 0;
          let description = '';
          
          dateLogs.forEach(log => {
            if (log.patients && log.patients.length > 0) {
              log.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                const subtotal = count * rate;
                const percentageRate = parseFloat(log.rateValue) || 0;
                
                // Calculate item amount exactly as done in Monthly invoice
                const itemAmount = subtotal * (percentageRate / 100);
                amount += itemAmount;
                
                // Build description in the same format as Monthly invoice
                const patientDesc = patient.description ? ` (${patient.description})` : '';
                description += `${count} × £${rate.toFixed(2)} @ ${percentageRate}%${patientDesc}, `;
                
                // Add patient with required fields for database
                allPatients.push({
                  count: parseInt(patient.count) || 0,
                  rate: parseFloat(patient.rate) || 0,
                  description: patient.description || '',
                  rateType: 'percentage',
                  rateValue: percentageRate
                });
                
                totalPatientCount += parseInt(patient.count) || 0;
              });
            }
          });
          
          // Trim trailing comma and space
          if (description.endsWith(', ')) {
            description = description.slice(0, -2);
          }
          
          return {
            date,
            formattedDate,
            amount: amount, // Use the calculated amount
            patients: allPatients,
            totalPatients: totalPatientCount,
            rateType: 'percentage',
            rateValue: parseFloat(dateLogs[0].rateValue) || 0,
            description,
          };
        } else if (dateLogs[0].rateType === 'per_patient') {
          // Combine all patients for this date (per_patient version)
          const allPatients = [];
          let totalPatientCount = 0;
          let amount = 0;
          let description = '';
          
          dateLogs.forEach(log => {
            if (log.patients && log.patients.length > 0) {
              log.patients.forEach(patient => {
                const count = parseInt(patient.count) || 0;
                const rate = parseFloat(patient.rate) || 0;
                
                // Calculate item amount (no percentage for per_patient)
                const itemAmount = count * rate;
                amount += itemAmount;
                
                // Build description in the same format as Monthly invoice (no percentage)
                const patientDesc = patient.description ? ` (${patient.description})` : '';
                description += `${count} × £${rate.toFixed(2)}${patientDesc}, `;
                
                // Add patient with required fields for database
                allPatients.push({
                  count: parseInt(patient.count) || 0,
                  rate: parseFloat(patient.rate) || 0,
                  description: patient.description || '',
                  rateType: 'per_patient'
                });
                
                totalPatientCount += parseInt(patient.count) || 0;
              });
            }
          });
          
          // Trim trailing comma and space
          if (description.endsWith(', ')) {
            description = description.slice(0, -2);
          }
          
          return {
            date,
            formattedDate,
            amount: amount, // Use the calculated amount
            patients: allPatients,
            totalPatients: totalPatientCount,
            rateType: 'per_patient',
            description,
          };
        } else {
          // For hourly or fixed rates
          const totalHours = dateLogs.reduce((sum, log) => sum + (parseFloat(log.hours) || 0), 0);
          const rateValue = parseFloat(dateLogs[0].rateValue) || 0;
          
          // Calculate amount based on rate type - EXACTLY as done in Monthly invoice
          const amount = dateLogs[0].rateType === 'hourly' 
            ? totalHours * rateValue 
            : rateValue;
          
          // Description format matching Monthly invoice
          let description = dateLogs[0].rateType === 'hourly'
            ? `${totalHours} hr${totalHours !== 1 ? 's' : ''} @ £${rateValue.toFixed(2)}/hr`
            : `Fixed rate: £${rateValue.toFixed(2)}`;

          // Append user-provided description if it exists across any log for the day
          const userDescriptions = dateLogs
            .map(log => log.description)
            .filter(desc => desc && desc.trim() !== '')
            .join('; '); // Join multiple descriptions if they exist

          if (userDescriptions) {
            description += ` (${userDescriptions})`;
          }

          return {
            date,
            formattedDate,
            amount,
            total: amount, // Add total field explicitly for PDF generator
            hours: totalHours,
            rate: rateValue, // Make sure this field is included
            rateType: dateLogs[0].rateType,
            rateValue: rateValue,
            description: description // Use the combined description
          };
        }
      });
      
      // Create invoice data for Firestore with EXACT structure
      const invoiceData = {
        userId: user.uid,
        practiceId,
        practiceDetails,
        invoiceNumber,
        totalAmount: parseFloat(totalAmount), // Ensure this is a number
        createdAt: new Date(),
        rateType: logs[0].rateType,
        rateValue: parseFloat(logs[0].rateValue) || 0,
        entriesCount: logs.length,
        summaryData, // Include the properly formatted summary data
        status: 'active'
      };
      
      // Save to Firestore
      const invoiceRef = collection(db, 'invoices');
      const invoiceDocRef = await addDoc(invoiceRef, invoiceData);
      console.log('💾 Invoice saved with ID:', invoiceDocRef.id);
      
      // Generate PDF if needed (this is now controlled by a parameter)
      // REMOVE THIS SECTION to prevent duplicate PDFs
      // if (generatePdf && profileData) {
      //   ... PDF generation code removed ...
      // }
      
      // Archive the logs
      console.log(`💾 Starting archive process for ${logIds.length} logs with invoice number: ${invoiceNumber}`);
      
      // Call the archive service with right parameters and options
      const archiveResult = await dailyLogService.archiveAndDeleteLogs(
        logIds, 
        invoiceNumber, 
        {
          skipPdfGeneration: true // Always skip PDF generation in archive process
        }
      );
      
      console.log('💾 Archive operation completed:', archiveResult);
      
      // Reload logs to update the UI
      setTimeout(() => {
        console.log('🔄 Reloading logs after archiving');
        loadLogs();
      }, 1000);
      
      toast({
        title: "Invoice Saved",
        description: `Invoice ${invoiceNumber} saved and records archived`,
        variant: "success",
      });
      
      return {
        success: true,
        invoiceId: invoiceDocRef.id,
        invoiceNumber
      };
    } catch (error) {
      console.error('💾 Error saving invoice and archiving logs:', error);
      toast({
        title: "Warning",
        description: "PDF was generated but there was an error saving the invoice data: " + error.message,
        variant: "warning",
      });
      return { success: false, error };
    }
  };

  // --- EMAIL SELF LOGIC (aligned with InvoiceDetails) ---
  const handleEmailSelf = async () => {
    if (!selectedPracticeForInvoice || !profileData) {
      toast({
        title: "Error",
        description: "Invoice details are not available.",
        variant: "destructive",
      });
      return;
    }
    try {
      setEmailLoading(true);
      // Reset PDF loading flags in case user previously generated a PDF or switched actions
      setPdfLoading(false);
      setPdfGenerationInProgress(false);
      // Ensure invoice number is set before generating PDF/email
      let invoiceNumber = currentInvoiceNumber;
      if (!invoiceNumber) {
        toast({
          title: "Generating Invoice Number",
          description: "Creating invoice number for email...",
        });
        invoiceNumber = await invoiceNumberService.generateInvoiceNumber(
          user.uid,
          profileData,
          { forSaving: true, persistToDatabase: true }
        );
        if (!invoiceNumber) {
          throw new Error("Failed to generate invoice number");
        }
        setCurrentInvoiceNumber(invoiceNumber);
        console.log(`Generated fresh invoice number for email: ${invoiceNumber}`);
      } else {
        console.log(`Using existing invoice number for email: ${invoiceNumber}`);
      }
      const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import('firebase/functions');
      let practice = selectedPracticeForInvoice;
      // Always resolve to full practice object (with address/name) for PDF/email
      if (!practice || typeof practice === 'string' || !practice.prac_name || !practice.address1) {
        // Try to find in selectedPractices by code
        let code = practice && typeof practice === 'string' ? practice : practice?.prac_code || practice?.practiceId;
        let found = selectedPractices.find(p => p.prac_code === code || p.practiceId === code);
        if (found) {
          practice = found;
        } else {
          // Fallback: try to fetch from service
          try {
            const { practiceDataUtil } = await import('../helpers/practiceDataUtil');
            const { practiceService } = await import('../services/practiceService');
            found = await practiceDataUtil.getPracticeByCode(code);
            if (found) practice = found;
            else {
              found = await practiceService.getPracticeById(code, null, user?.uid);
              if (found) practice = found;
            }
          } catch (e) {
            console.warn('Failed to resolve practice by code/id:', e);
          }
        }
      }
      const selectedLogs = selectedLogsForPdf.filter(log => log.selected !== false);
      const pdfBlob = await generateInvoicePDFBlob({
        practice,
        entries: selectedLogs,
        total: selectedLogs.reduce((sum, log) => sum + calculateLogTotal(log), 0),
        invoiceNumber,
        profile: profileData,
        rateType: selectedLogs[0]?.rateType || rateType,
        rateValue: selectedLogs[0]?.rateValue || rateValue
      });
      const blobToBase64 = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const pdfBase64 = await blobToBase64(pdfBlob);
      const userEmail = profileData?.email;
      // Use the invoice number that was actually used for the PDF (from the PDF generator call)
      const usedInvoiceNumber = invoiceNumber;
      const practiceName = (practice?.prac_name || practice?.name)?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
      const filename = `${practiceName}_invoice_${usedInvoiceNumber || new Date().toISOString().split('T')[0]}.pdf`;
      const subject = `Invoice ${usedInvoiceNumber} from ${profileData?.name || 'Dental Hygienist'}`;
      const fullName = profileData?.name || 'Dental Hygienist';
      const html = `Please find your invoice attached.<br><br>Kind regards,<br>${fullName}`;
      const plainText = `Please find your invoice attached.\n\nKind regards,\n${fullName}`;
      const functions = getFunctions();
      if (window.location.hostname === 'localhost') {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      const sendEmailWithAttachment = httpsCallable(functions, 'sendEmailWithAttachment');
      const result = await sendEmailWithAttachment({
        to: userEmail,
        subject,
        html,
        plainText,
        filename,
        pdfBase64,
        name: fullName
      });
      if (result.data && result.data.success) {
        setConfirmationModalContent({
          title: 'Invoice Sent!',
          description: 'Your invoice PDF has been emailed to you.'
        });
        setShowConfirmationModal(true);
      } else {
        toast({
          title: "Email Failed",
          description: "Sorry, we couldn't send your invoice email.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error emailing invoice:", error);
      toast({
        title: "Email Failed",
        description: "Sorry, we couldn't send your invoice email.",
        variant: "destructive",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Function to handle date selection and close calendar
  const handleDateSelect = (newDate) => {
    if (newDate) {
      setDate(newDate);
      setCalendarOpen(false); // Close the calendar popover
    }
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  // Calculate total for a log entry
  const calculateLogTotal = (log) => {
    if (!log) return 0;

    if (log.rateType === 'percentage') {
      // Handle percentage calculations
      if (log.patients && Array.isArray(log.patients)) {
        // If we have detailed patient data
        return log.patients.reduce((total, patient) => {
          const count = parseInt(patient.count) || 0;
          const rate = parseFloat(patient.rate) || 0;
          const subtotal = count * rate;
          const percentageRate = parseFloat(log.rateValue) || 0;
          return total + (subtotal * (percentageRate / 100));
        }, 0);
      } else {
        // Fallback to patientCount * rate * percentage
        const patientCount = parseInt(log.patientCount) || 0;
        const rate = parseFloat(log.rate) || 0;
        const percentageRate = parseFloat(log.rateValue) || 0;
        return (patientCount * rate) * (percentageRate / 100);
      }
    } else if (log.rateType === 'per_patient') {
      // Handle per_patient calculations (no percentage)
      if (log.patients && Array.isArray(log.patients)) {
        // If we have detailed patient data
        return log.patients.reduce((total, patient) => {
          const count = parseInt(patient.count) || 0;
          const rate = parseFloat(patient.rate) || 0;
          return total + (count * rate); // No percentage multiplier
        }, 0);
      } else {
        // Fallback to patientCount * rate
        const patientCount = parseInt(log.patientCount) || 0;
        const rate = parseFloat(log.rate) || 0;
        return patientCount * rate;
      }
    } else if (log.rateType === 'hourly') {
      const hours = parseFloat(log.hours || log.workHours) || 0;
      const hourlyRate = parseFloat(log.rateValue) || 0;
      return hours * hourlyRate;
    } else if (log.rateType === 'fixed') {
      return parseFloat(log.rateValue) || 0;
    }
    
    return 0;
  };

  // Get unique months from logs for the filter
  const getAvailableMonths = () => {
    const lastSixMonths = [];
    const today = new Date();
    
    for (let i = 0; i < 6; i++) {
      const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
      
      // Add defensive check to ensure month is a valid Date
      if (month instanceof Date && !isNaN(month.getTime())) {
        lastSixMonths.push({
          value: format(month, 'yyyy-MM'),
          label: format(month, 'MMMM yyyy')
        });
      }
    }
    
    return lastSixMonths;
  };

  // Get actual months with records for dropdown
  const getMonthsWithRecords = () => {
    // Start with "All" option
    const monthOptions = [{ value: 'all', label: 'All Records' }];
    
    if (!logs || logs.length === 0) {
      // Add recent months as options if no logs exist yet
      const recentMonths = getAvailableMonths();
      return [...monthOptions, ...recentMonths];
    }
    
    // Extract unique months from logs
    const uniqueMonths = new Map();
    logs.forEach(log => {
      // Add defensive check to ensure log.date is a valid Date object
      if (log.date && log.date instanceof Date && !isNaN(log.date.getTime())) {
        const month = format(log.date, 'yyyy-MM');
        const label = format(log.date, 'MMMM yyyy');
        uniqueMonths.set(month, label);
      } else {
        console.warn('Invalid date detected in log:', log);
      }
    });
    
    // Sort months in descending order (newest first)
    const sortedMonths = Array.from(uniqueMonths.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([value, label]) => ({ value, label }));
    
    return [...monthOptions, ...sortedMonths];
  };

  // Group logs by practice
  const logsByPractice = logs.reduce((acc, log) => {
    if (!acc[log.practiceId]) {
      acc[log.practiceId] = {
        practice: log.practiceName,
        practiceId: log.practiceId,
        logs: []
      };
    }
    acc[log.practiceId].logs.push(log);
    return acc;
  }, {});

  // Calculate total hours for a practice (for hourly/fixed rate records)
  const calculateTotalHours = (logs) => {
    // Make sure logs is an array
    if (!Array.isArray(logs) || logs.length === 0) {
      return '0.0';
    }
    
    return logs
      .filter(log => log?.rateType === 'hourly' || log?.rateType === 'fixed')
      .reduce((total, log) => total + (parseFloat(log?.workHours || log?.hours) || 0), 0)
      .toFixed(1);
  };

  // Calculate total patients for a practice (for percentage and per_patient rate records)
  const calculateTotalPatients = (logs) => {
    // Make sure logs is an array
    if (!Array.isArray(logs) || logs.length === 0) {
      return 0;
    }
    
    return logs
      .filter(log => log?.rateType === 'percentage' || log?.rateType === 'per_patient')
      .reduce((total, log) => total + (parseInt(log?.patientCount) || 0), 0);
  };

  // Format rate based on rate type
  const formatRate = (log) => {
    if (!log.rateValue) return '-';
    
    if (log.rateType === 'percentage') {
      return `${log.rateValue}%`;
    } else {
      return `£${log.rateValue}`; // Currency symbol before number
    }
  };

  // Get log description based on rate type
  const getLogDescription = (log) => {
    if (!log) return '';
    
    if (log.rateType === 'percentage') {
      if (log.patients && Array.isArray(log.patients) && log.patients.length > 0) {
        // Enhanced format with percentage values
        return log.patients.map(p => 
          `${p.count} × £${parseFloat(p.rate).toFixed(2)} @ ${log.rateValue}%${p.description ? ` (${p.description})` : ''}`
        ).join(', ');
      } else {
        // Fallback to basic description with percentage
        return `${log.patientCount || 0} patient${log.patientCount !== 1 ? 's' : ''} @ ${log.rateValue}%`;
      }
    } else if (log.rateType === 'per_patient') {
      if (log.patients && Array.isArray(log.patients) && log.patients.length > 0) {
        // Per patient format (no percentage)
        return log.patients.map(p => 
          `${p.count} × £${parseFloat(p.rate).toFixed(2)}${p.description ? ` (${p.description})` : ''}`
        ).join(', ');
      } else {
        // Fallback to basic description (no percentage)
        return `${log.patientCount || 0} patient${log.patientCount !== 1 ? 's' : ''}`;
      }
    } else if (log.rateType === 'hourly') {
      const hours = parseFloat(log.hours || log.workHours) || 0;
      return `${hours} hr${hours !== 1 ? 's' : ''} @ £${parseFloat(log.rateValue).toFixed(2)}/hr`;
    } else if (log.rateType === 'fixed') {
      return `Fixed rate: £${parseFloat(log.rateValue).toFixed(2)}`;
    }
    
    return '';
  };

  // Get practice list for selected logs
  const getSelectedPractices = () => {
    const selectedLogsList = logs.filter(log => log.selected);
    const practices = {};
    
    selectedLogsList.forEach(log => {
      if (!practices[log.practiceId]) {
        practices[log.practiceId] = {
          practiceId: log.practiceId,
          practiceName: log.practiceName,
          count: 0,
          totalAmount: 0
        };
      }
      practices[log.practiceId].count++;
      practices[log.practiceId].totalAmount += calculateLogTotal(log);
    });
    
    return Object.values(practices);
  };

  // Handle invoice generation confirmation
  const handleConfirmInvoiceGeneration = async () => {
    if (selectedLogs.length === 0) return;
    setGenerateLoading(true);
    try {
      // Implementation for invoice generation would go here
      // This is a placeholder that shows a toast message
      
      toast({
        title: "Invoice Generated",
        description: "Your invoice was generated successfully",
        variant: "success",
      });
      
      setShowInvoiceDialog(false);
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast({
        title: "Error",
        description: "Failed to generate invoice",
        variant: "destructive",
      });
    } finally {
      setGenerateLoading(false);
    }
  };

  // Effect to reload logs when archive toggle changesad logs when archive toggle changes
  useEffect(() => {
    loadLogs();
  }, [showArchivedRecords, archivePeriod]);

  // Handle unarchive function
  const handleUnarchive = (log) => {
    setLogToUnarchive(log);
    setShowUnarchiveDialog(true);
  };

  // Add this new utility function to explicitly load active records
  const forceLoadActiveRecords = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      console.log('🔍 Explicitly loading ACTIVE records after unarchive');
      
      // Create options that explicitly set showArchived to false
      const options = {
        invoiced: false,
        forceRefresh: true  // Force server refresh
      };
      
      // Apply any active filters
      if (filterMonth !== 'all') {
        const [year, month] = filterMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0, 23, 59, 59);
        options.startDate = startDate;
        options.endDate = endDate;
      }
      
      if (filterPractice !== 'all') {
        options.practiceId = filterPractice;
      }
      
      // Get active logs directly
      const result = await dailyLogService.getLogEntries(user.uid, options);
      console.log(`🔍 Forced active records query returned ${result.logs.length} logs`);
      
      // Update UI state directly
      setLogs(result.logs);
      setArchivedLogs([]);
      
    } catch (error) {
      console.error("❌ Error loading active records:", error);
      toast({
        title: "Error",
        description: "Failed to load your records",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Improved unarchive function with better view switching
  const confirmUnarchive = async () => {
    if (!logToUnarchive) return;
    setUnarchiveLoading(true);
    try {
      console.log(`🔁 Starting unarchive process for: ${logToUnarchive.id}, User: ${user.uid}`);
      
      // First show a loading toast
      toast({
        title: "Restoring Record",
        description: "Searching archive and restoring record...",
      });
      
      // Call unarchive function
      const result = await dailyLogService.unarchiveLog(logToUnarchive.id);
      
      if (result && result.success) {
        console.log('🔁 Unarchive successful with NEW document ID:', {
          newLogId: result.newLogId,
          archiveId: result.archiveId
        });
        
        // Show success message
        toast({
          title: "Success",
          description: "Record restored successfully"
        });
        
        const updatedLogs = logs.filter(log => log.id !== logToUnarchive.id);
        setLogs(updatedLogs);
        
        // CRITICAL: Force switch to active records view AFTER updating logs
        console.log('🔁 Switching to active records view');
        setShowArchivedRecords(false);
        
        // IMPORTANT CHANGE: Use our explicit function instead of regular loadLogs
        // This fixes the closure issue where the timeout captures stale state
        setTimeout(() => {
          console.log('🔄 Forcing load of ACTIVE records with explicit state');
          forceLoadActiveRecords(); // Use our new function instead of loadLogs
        }, 2000);
      }
    } catch (error) {
      console.error("Error unarchiving log:", error);
      
      // Display debug logs if available
      if (error.debugLog) {
        console.log("Unarchive debug log:", error.debugLog);
      }
      
      let errorMessage = "Failed to restore record";
      
      // Check status code and provide more helpful message
      if (error.message && (error.message.includes("not found") || error.message.includes("No archived logs found"))) {
        errorMessage = "Record not found in archive. It may have been already restored or deleted.";
      } else if (error.message && error.message.includes("Permission denied")) {
        errorMessage = "Permission denied: Unable to restore this record.";
      } else {
        errorMessage = `Error restoring record: ${error.message}`;
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setUnarchiveLoading(false);
      setShowUnarchiveDialog(false);
      setLogToUnarchive(null);
    }
  };

  // Update the email function to use component state instead of window global
  const handleEmailInvoice = () => {
    if (!generatedPdfFilename) {
      toast({
        title: "Error",
        description: "Please generate a PDF first",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Find practice in selectedPractices
      const practiceId = selectedLogsForPdf[0].practiceId;
      const practice = selectedPractices.find(p => p.prac_code === practiceId);
      const selectedLogsData = selectedLogsForPdf.filter(log => log.selected !== false);
      
      // Calculate total amount
      const totalAmount = selectedLogsData.reduce((sum, log) => sum + calculateLogTotal(log), 0);
      
      // Get formatted email content - also pass the logs for treatment dates
      const emailDetails = formatInvoiceEmail({
        practiceName: practice?.prac_name,
        totalAmount,
        invoiceNumber: currentInvoiceNumber,
        filename: generatedPdfFilename, // Use component state instead of window global
        profileData,
        formatCurrency,
      });
      
      // Create and open mailto link
      const mailtoLink = `mailto:${encodeURIComponent(practice?.email || '')}?subject=${encodeURIComponent(emailDetails.subject)}&body=${encodeURIComponent(emailDetails.body)}`;
      window.location.href = mailtoLink;
      
      // Close the dialog if open
      setShowEmailDialog(false);
    } catch (error) {
      console.error("Error preparing email:", error);
      toast({
        title: "Error",
        description: "Failed to prepare email: " + error.message,
        variant: "destructive",
      });
    }
  };


  // --- Invoice Preview State & Handlers (single source of truth) ---
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewInvoiceNumber, setPreviewInvoiceNumber] = useState('PREVIEW');
  const [invoiceNumberLoading, setInvoiceNumberLoading] = useState(false);

  // Async handler to generate preview invoice number (non-incrementing, non-persisting)
  const handlePreviewInvoice = async () => {
    if (invoiceNumberLoading) return;
    setInvoiceNumberLoading(true);
    try {
      let invoiceNum = previewInvoiceNumber;
      if (!invoiceNum || invoiceNum === 'PREVIEW') {
        const { invoiceNumberService } = await import('../services/invoiceNumberService');
        invoiceNum = await invoiceNumberService.getPreviewNumber(
          user?.uid,
          profileData
        );
        setPreviewInvoiceNumber(invoiceNum);
      }
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Failed to generate preview invoice number:', err);
      setPreviewInvoiceNumber('PREVIEW');
    } finally {
      setInvoiceNumberLoading(false);
    }
  };

  // Helper for preview modal to always use the non-incrementing preview number
  const getPreviewInvoiceNumber = () => previewInvoiceNumber || 'PREVIEW';

  // (Removed duplicate handleCloseInvoiceDialog; see async version below)

  // New function to force a server refresh of active logs
  const loadActiveLogsWithServerRefresh = async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      console.log('🔄 Performing server-refresh of active logs');
      
      // Prepare query options - force active records view
      const options = {
        invoiced: false,
        forceRefresh: true  // New flag to force server read
      };
      
      // Apply any active filters
      if (filterMonth !== 'all') {
        const [year, month] = filterMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0, 23, 59, 59);
        options.startDate = startDate;
        options.endDate = endDate;
      }
      
      if (filterPractice !== 'all') {
        options.practiceId = filterPractice;
      }
      
      // Directly modify dailyLogService.getLogEntries to support forceRefresh option
      const result = await dailyLogService.getLogEntries(user.uid, options);
      console.log(`🔄 Server refresh returned ${result.logs.length} logs`);
      
      // Update state with fresh data
      setLogs(result.logs);
      setArchivedLogs([]);
    } catch (error) {
      console.error("❌ Error refreshing logs:", error);
      toast({
        title: "Error",
        description: "Failed to refresh your records",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function near the top of the component after your state declarations
  const safeFormatDate = (dateObj, formatStr, fallbackText = 'Unknown date') => {
    try {
      // First check if dateObj exists and is a valid Date
      if (dateObj && dateObj instanceof Date && !isNaN(dateObj.getTime())) {
        return format(dateObj, formatStr);
      } else {
        console.warn('Invalid date encountered:', dateObj);
        return fallbackText;
      }
    } catch (error) {
      console.error('Error formatting date:', error, dateObj);
      return fallbackText;
    }
  };

  // Handle saving practices from practice settings popup
  // IMPORTANT: This must complete the Firestore save before closing the dialog
  const handleSavePractices = async (practices, newlyAddedPracticeCode = null) => {
    try {
      console.log('[DailyRecordLog] handleSavePractices called v2', {
        practicesCount: practices?.length,
        newlyAddedPracticeCode,
        userId: user?.uid,
        dbAvailable: !!db
      });

      if (!user?.uid) {
        console.error('[DailyRecordLog] No user ID - cannot save');
        toast({
          title: "Error",
          description: "Unable to save practices. Please try logging in again.",
          variant: "destructive"
        });
        return;
      }

      // 1. Save to Firestore FIRST (before any state updates)
      const userDocRef = doc(db, 'users', user.uid);
      const dataToSave = {
        selectedPractices: practices.map(p => p.prac_code || p),
        lastUpdated: new Date()
      };
      console.log('[DailyRecordLog] Saving to Firestore:', userDocRef.path, dataToSave);
      
      await updateDoc(userDocRef, dataToSave);
      console.log('[DailyRecordLog] ✅ Firestore save complete');

      // 2. Update local state AFTER successful save
      setSelectedPractices(practices);

      // 3. Auto-select logic
      if (newlyAddedPracticeCode) {
        console.log('[DailyRecordLog] Auto-selecting newly added:', newlyAddedPracticeCode);
        setSelectedPractice(newlyAddedPracticeCode);
      } else if (practices.length > 0 && !practices.find(p => p.prac_code === selectedPractice)) {
        console.log('[DailyRecordLog] Switching to first practice:', practices[0].prac_code);
        setSelectedPractice(practices[0].prac_code);
      }

      // 4. Reload from context to ensure sync
      await loadUserSelectedPractices(user.uid);

      // 5. Close dialog and show success
      setShowPracticeSettings(false);
      toast({
        title: "Success",
        description: newlyAddedPracticeCode 
          ? "Practice added and selected successfully"
          : "Practice selection saved successfully",
      });
    } catch (error) {
      console.error('[DailyRecordLog] ❌ handleSavePractices error:', error);
      console.error('[DailyRecordLog] Error details:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      toast({
        title: "Error",
        description: `Failed to save practice selection: ${error.message || 'Unknown error'}. Please try again.`,
        variant: "destructive"
      });
    }
  };


  // --- State to track if current selection has been invoiced (PDF or Email) ---
  const [hasInvoicedCurrentSelection, setHasInvoicedCurrentSelection] = useState(false);

  // --- Confirmation Modal State for PDF/Email actions ---
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalContent, setConfirmationModalContent] = useState({ title: '', description: '' });

  // Reset invoiced state when selection changes or modal is opened/closed
  useEffect(() => {
    setHasInvoicedCurrentSelection(false);
  }, [selectedLogs, showInvoiceDialog]);

  // Helper: Archive selected logs if invoiced
  const handleArchiveIfInvoiced = async () => {
    if (hasInvoicedCurrentSelection) {
      // Archive logic here (archive only selected logs)
      try {
        await archiveSelectedLogs();
        toast({ title: 'Records Archived', description: 'Selected records have been archived after invoicing.' });
      } catch (err) {
        toast({ title: 'Archive Error', description: 'Failed to archive records.', variant: 'destructive' });
      }
    }
  };

  // Patch: Only archive on close if invoiced
  const handleCloseInvoiceDialog = async () => {
    setShowInvoiceDialog(false);
    setPdfGenerationInProgress(false);
    setPdfLoading(false);
    setGeneratedPdfFilename(null);
    await handleArchiveIfInvoiced();
    setTimeout(() => {
      loadLogs();
    }, 500);
  };


  // Archive selected logs utility (implement as needed)
  const archiveSelectedLogs = async () => {
    // TODO: Implement archiving logic or remove this function if not needed
    // Example: await dailyLogService.archiveLogs(selectedLogsForPdf.map(l => l.id));
  };

  // ...existing code...
  // (Removed stray/duplicate logic and misplaced return)
  
  return (
  <div className="space-y-6">
    {/* Daily draft restore banner */}
    {dailyDraft && (() => {
      const draftPracticeName = dailyDraft.selectedPractice
        ? (selectedPractices.find(p => typeof p === 'object' && p.prac_code === dailyDraft.selectedPractice)?.prac_name || null)
        : null;
      return (
        <div className={cn(
          'flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm',
          theme === 'light'
            ? 'bg-amber-50 border-amber-300 text-amber-900'
            : 'bg-amber-900/20 border-amber-600/40 text-amber-300'
        )}>
          <span>
            📋 You have an unsaved daily record draft
            {draftPracticeName ? <> for <strong>{draftPracticeName}</strong></> : ''}.
            {' '}Restore it?
          </span>
          <div className="flex gap-3 shrink-0 items-center">
            <button
              onClick={handleRestoreDailyDraft}
              className="font-semibold underline underline-offset-2 hover:opacity-80"
            >
              Restore
            </button>
            <span className="opacity-30">|</span>
            <button
              onClick={dismissDailyDraft}
              className="opacity-60 hover:opacity-90 underline underline-offset-2"
              aria-label="Discard draft"
            >
              Discard
            </button>
          </div>
        </div>
      );
    })()}
    {/* Nuclear CSS override for light mode inputs */}

      {theme === 'light' && (
        <style dangerouslySetInnerHTML={{
          __html: `
            /* Force white backgrounds for ALL inputs in light mode */
            input[type="text"],
            input[type="number"],
            input[type="email"],
            input:not([type]) {
              background-color: white !important;
              background: white !important;
            }
            
            /* Target by class names too */
            .bg-white {
              background-color: white !important;
              background: white !important;
            }
            
            /* Override any hover/focus states */
            input[type="text"]:hover,
            input[type="number"]:hover,
            input[type="email"]:hover,
            input:not([type]):hover {
              background-color: white !important;
              background: white !important;
            }
            
            input[type="text"]:focus,
            input[type="number"]:focus,
            input[type="email"]:focus,
            input:not([type]):focus {
              background-color: white !important;
              background: white !important;
            }
          `
        }} />
      )}

      {/* Daily Record Entry - Redesigned for better UX */}
      <Card className={cn(
        "shadow-lg",
        theme === 'light' ? "bg-white border-gray-200" : "bg-[#2A2A3E] border-purple-500/20"
      )}>
        <CardContent className="p-4">
          <h2 className={cn(
            "text-lg font-medium mb-4",
            theme === 'light' ? "text-gray-900" : "text-white"
          )}>Add Daily Work Record</h2>
          {/* Add Practice Selection Dropdown as first item */}
          <div className="mb-4">
      {/* Date Selector */}
      <div className="mb-4">
        <Label className={cn(
          "text-sm mb-1 block",
          theme === 'light' ? "text-gray-700" : "text-gray-300"
        )}>Date</Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !date && "text-muted-foreground",
                theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {date ? format(date, "do MMM, yyyy") : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className={cn(
            theme === 'light' ? "bg-white border-gray-300" : "bg-[#2A2A3E] border-purple-500/30"
          )} align="start">
            <SimpleCalendar
              value={date}
              onChange={handleDateSelect}
            />
          </PopoverContent>
        </Popover>
      </div>
            <Label className={cn(
              "text-sm mb-1 block",
              theme === 'light' ? "text-gray-700" : "text-gray-300"
            )}>Practice</Label>
            <div className="flex space-x-2 items-center">
              <Select
                value={selectedPractice}
                onValueChange={setSelectedPractice}
                className="flex-1"
              >
                <SelectTrigger className={cn(
                  "border",
                  theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                )}>
                  <SelectValue placeholder={selectedPractices.length > 0 ? "Select practice" : "No practices available"} />
                </SelectTrigger>
                <SelectContent className={cn(
                  "border",
                  theme === 'light' ? "bg-white border-gray-300" : "bg-[#2A2A3E] border-purple-500/30"
                )}>
                  {selectedPractices.length > 0 ? (
                    selectedPractices.map(practice => (
                      <SelectItem 
                        key={practice.prac_code} 
                        value={practice.prac_code}
                        className={cn(
                          theme === 'light' ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-purple-500/20"
                        )}
                      >
                        {practice.prac_name} {practice.postcode ? `(${practice.postcode})` : ''}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled className="text-gray-500">
                      No practices available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                className={cn(
                  "text-xs",
                  theme === 'light' 
                    ? "border-gray-300 bg-gray-50 text-gray-700" 
                    : "border-purple-500/30 bg-purple-900/20 text-purple-300"
                )}
                onClick={() => setShowPracticeSettings(true)}
              >
                <Settings className="h-3 w-3 mr-1" />
                Manage
              </Button>
            </div>
          </div>
      {/* Rate Type Selector */}
      {/* Rate Details Section */}
      <div className="mb-4">
        <Label className={cn(
          "text-sm mb-1 block",
          theme === 'light' ? "text-gray-700" : "text-gray-300"
        )}>Rate Details</Label>
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <Select value={rateType} onValueChange={setRateType}>
              <SelectTrigger className={cn(
                "border",
                theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
              )}>
                <SelectValue placeholder="Select rate type" />
              </SelectTrigger>
              <SelectContent className={cn(
                "border",
                theme === 'light' ? "bg-white border-gray-300" : "bg-[#2A2A3E] border-purple-500/30"
              )}>
                <SelectItem value="percentage">Percentage</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="per_patient">Per Patient</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {rateType === 'percentage' && (
            <div className="flex items-center gap-1 w-40">
              <Input
                id="rateValue"
                type="number"
                value={rateValue}
                step="0.01"
                onChange={(e) => setRateValue(e.target.value)}
                className={cn(
                  theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                )}
                placeholder="e.g., 50"
              />
              <span className={cn("text-base font-medium", theme === 'light' ? "text-gray-700" : "text-gray-300")}>%</span>
            </div>
          )}
          {rateType === 'hourly' && (
            <div className="flex items-center gap-1 w-48">
              <Input
                id="rateValue"
                type="number"
                value={rateValue}
                step="0.01"
                onChange={(e) => setRateValue(e.target.value)}
                className={cn(
                  theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                )}
                placeholder="e.g., 35"
              />
              <span className={cn("text-base font-medium", theme === 'light' ? "text-gray-700" : "text-gray-300")}>£/hr</span>
            </div>
          )}
          {rateType === 'fixed' && (
            <div className="flex items-center gap-1 w-48">
              <Input
                id="rateValue"
                type="number"
                value={rateValue}
                step="0.01"
                onChange={(e) => setRateValue(e.target.value)}
                className={cn(
                  theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                )}
                placeholder="e.g., 250"
              />
              <span className={cn("text-base font-medium", theme === 'light' ? "text-gray-700" : "text-gray-300")}>£</span>
            </div>
          )}
        </div>
      </div>

      {/* Conditional Inputs for Rate Types */}
      {(rateType === 'percentage' || rateType === 'per_patient') && (
        <>
          <Label className={cn(
            "text-sm mb-1 block mt-4",
            theme === 'light' ? "text-gray-700" : "text-gray-300"
          )}>Patient Details</Label>
          <div className="mb-4 flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={cn(
                "h-8",
                theme === 'light' 
                  ? "border-purple-300 bg-purple-50 text-purple-700"
                  : "border-purple-500/30 bg-purple-900/20 text-purple-300"
              )}
              onClick={handleAddPatient}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Patient Type
            </Button>
          </div>
          <div>
            {patients.map((patient, index) => (
              <div key={index} className={cn(
                "mb-3 p-3 rounded-lg border",
                theme === 'light' 
                  ? "bg-gray-50 border-gray-200" 
                  : "bg-[#2A2A3E] border-purple-500/10"
              )}>
                <div className="flex justify-between items-center mb-2">
                  <span className={cn(
                    "text-xs font-medium",
                    theme === 'light' ? "text-gray-700" : "text-purple-300"
                  )}>Patient Type {index + 1}</span>
                  {patients.length > 1 && (
                    <Button 
                      type="button"
                      size="sm"
                      variant="ghost"
                      className={cn(
                        "h-7 w-7 p-0",
                        theme === 'light' 
                          ? "text-red-600 hover:text-red-700 hover:bg-red-100" 
                          : "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      )}
                      onClick={() => handleRemovePatient(index)}
                    >
                      <MinusIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-2">
                    <Label htmlFor={`count-${index}`} className={cn(
                      "text-xs mb-1 block",
                      theme === 'light' ? "text-gray-600" : "text-gray-400"
                    )}>Count</Label>
                    <Input
                      id={`count-${index}`}
                      type="number"
                      value={patient.count}
                      onChange={(e) => handlePatientChange(index, 'count', e.target.value)}
                      className={cn(
                        theme === 'light' 
                          ? "bg-white border-gray-300 text-gray-900" 
                          : "bg-[#2A2A3E] border-gray-700 text-white"
                      )}
                      placeholder="# pts"
                      maxLength="2"
                    />
                  </div>
                  <div className="col-span-3">
                    <Label htmlFor={`rate-${index}`} className={cn(
                      "text-xs mb-1 block",
                      theme === 'light' ? "text-gray-600" : "text-gray-400"
                    )}>Rate (£)</Label>
                    <Input
                      id={`rate-${index}`}
                      type="number"
                      value={patient.rate}
                      step="0.01"
                      onChange={(e) => handlePatientChange(index, 'rate', e.target.value)}
                      className={cn(
                        theme === 'light' 
                          ? "bg-white border-gray-300 text-gray-900" 
                          : "bg-[#2A2A3E] border-gray-700 text-white"
                      )}
                      placeholder="£"
                      maxLength="4"
                    />
                  </div>
                  <div className="col-span-7">
                    <Label htmlFor={`description-${index}`} className={cn(
                      "text-xs mb-1 block",
                      theme === 'light' ? "text-gray-600" : "text-gray-400"
                    )}>Description</Label>
                    <Input
                      id={`description-${index}`}
                      value={patient.description}
                      onChange={(e) => handlePatientChange(index, 'description', e.target.value)}
                      className={cn(
                        theme === 'light' 
                          ? "bg-white border-gray-300 text-gray-900" 
                          : "bg-[#2A2A3E] border-gray-700 text-white"
                      )}
                      placeholder="e.g., Hygiene Treatment"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Only show Hours Worked field for hourly rate type */}
      {rateType === 'hourly' && (
        <div className="mb-4">
          <Label htmlFor="hours" className={cn(
            "text-sm mb-1 block",
            theme === 'light' ? "text-gray-700" : "text-gray-300"
          )}>Hours Worked</Label>
          <Input
            id="hours"
            type="number"
            value={hours}
            step="0.25"
            onChange={(e) => setHours(e.target.value)}
            className={cn(
              theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
            )}
            placeholder="e.g., 7.5"
          />
        </div>
      )}
      {/* Removed duplicate Hourly Rate field for hourly type */}

      {/* Description (Optional) - Only for hourly and fixed */}
      {(rateType === 'hourly' || rateType === 'fixed') && (
        <div className="mb-4">
          <Label htmlFor="description" className={cn(
            "text-sm mb-1 block",
            theme === 'light' ? "text-gray-700" : "text-gray-300"
          )}>Description (Optional)</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={cn(
              theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
            )}
            placeholder="Additional information"
          />
        </div>
      )}
          {/* Save button with confirmation message */}
          <Button
            onClick={handleSave}
            disabled={saveInProgress}
            className="bg-purple-600 hover:bg-purple-700 text-white w-full"
          >
            {saveInProgress ? <LoadingSpinner size="small" /> : "Save Record"}
          </Button>
          {saveConfirmation && (
            <div className={cn(
              "mt-4 p-2 rounded-lg text-sm flex items-center",
              saveConfirmation.type === "success" 
                ? "bg-green-500/30 text-green-300 border border-green-500/30" 
                : "bg-red-500/20 text-red-300 border border-red-500/30"
            )}>
              <div className={cn(
                "flex-shrink-0 rounded-full flex items-center justify-center w-4 h-4 mr-2",
                saveConfirmation.type === "success" ? "bg-green-500/30" : "bg-red-500/30"
              )}>
                {saveConfirmation.type === "success" ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              {saveConfirmation.message}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Saved Records - Improved layout with better contrast and mobile responsiveness */}
      <Card className={cn(
        "shadow-lg",
        theme === 'light' ? "bg-white border-gray-200" : "bg-[#2A2A3E] border-purple-500/20"
      )}>
        <CardContent className="p-4">
          <div className="flex flex-col space-y-3">
            <div className="flex flex-wrap justify-between items-center">
              <h2 className={cn(
                "text-lg font-medium",
                theme === 'light' ? "text-gray-900" : "text-white"
              )}>
                {showArchivedRecords ? 'Archived Records' : 'Active Records'}
              </h2>
              <div className="flex items-center gap-2">
                <Switch
                  checked={showArchivedRecords}
                  onCheckedChange={setShowArchivedRecords}
                  className="mr-1"
                />
                <Label htmlFor="show-archived" className={cn(
                  "text-xs",
                  theme === 'light' ? "text-gray-700" : "text-gray-300"
                )}>
                  Show Archived
                </Label>
                {showArchivedRecords && (
                  <Select
                    value={archivePeriod}
                    onValueChange={setArchivePeriod}
                    className="ml-2"
                  >
                    <SelectTrigger className={cn(
                      "w-[100px] h-8 text-xs",
                      theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                    )}>
                      <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent className={cn(
                      theme === 'light' ? "bg-white border-gray-300" : "bg-[#2A2A3E] border-purple-500/30"
                    )}>
                      <SelectItem value="30" className={cn(theme === 'light' ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-purple-500/20", "text-xs")}>Last 30 Days</SelectItem>
                      <SelectItem value="60" className={cn(theme === 'light' ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-purple-500/20", "text-xs")}>Last 60 Days</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Select
                value={filterMonth}
                onValueChange={setFilterMonth}
              >
                <SelectTrigger className={cn(
                  "w-[160px]",
                  theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                )}>
                  <SelectValue placeholder="Filter by month" />
                </SelectTrigger>
                <SelectContent className={cn(
                  theme === 'light' ? "bg-white border-gray-300" : "bg-[#2A2A3E] border-purple-500/30"
                )}>
                  {getMonthsWithRecords().map(month => (
                    <SelectItem 
                      key={month.value} 
                      value={month.value}
                      className={cn(
                        theme === 'light' ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-purple-500/20"
                      )}
                    >
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filterPractice}
                onValueChange={(value) => {
                  console.log('Practice filter changed:', value);
                  setFilterPractice(value);
                }}
              >
                <SelectTrigger className={cn(
                  "w-[180px]",
                  theme === 'light' ? "bg-white border-gray-300 text-gray-900" : "bg-[#1F1F2E] border-purple-500/30 text-white"
                )}>
                  <SelectValue placeholder="Filter by practice" />
                </SelectTrigger>
                <SelectContent className={cn(
                  theme === 'light' ? "bg-white border-gray-300" : "bg-[#2A2A3E] border-purple-500/30"
                )}>
                  <SelectItem 
                    value="all" 
                    className={cn(
                      theme === 'light' ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-purple-500/20"
                    )}
                  >
                    All Practices
                  </SelectItem>
                  {selectedPractices.map(practice => (
                    <SelectItem 
                      key={practice.prac_code} 
                      value={practice.prac_code}
                      className={cn(
                        theme === 'light' ? "text-gray-900 hover:bg-gray-100" : "text-white hover:bg-purple-500/20"
                      )}
                    >
                      {practice.prac_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Continue with remaining UI elements using similar pattern */}
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="large" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8">
              <p className={cn(theme === 'light' ? "text-gray-600" : "text-gray-400")}>{showArchivedRecords ? 'No archived records found' : 'No active records found'} for the selected filters.</p>
              <p className={cn(theme === 'light' ? "text-gray-600" : "text-gray-400")}>{showArchivedRecords ? 'Try changing your filters or the archive period.' : 'Try changing your filters or add new daily records.'}</p>
            </div>
          ) : (
            Object.entries(logsByPractice).map(([practiceId, { practice, logs, practiceId: id }]) => {
              const hasHourlyOrFixed = logs.some(log => log.rateType === 'hourly' || log.rateType === 'fixed');
              const hasPercentage = logs.some(log => log.rateType === 'percentage' || log.rateType === 'per_patient');
              return (
                <div 
                  key={practiceId} 
                  className={cn(
                    "rounded-lg p-4",
                    theme === 'light' ? "bg-gray-50" : "bg-[#2A2A3E]"
                  )}
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                    <div>
                      <h3 className={cn(
                        "font-medium",
                        theme === 'light' ? "text-purple-700" : "text-purple-300"
                      )}>{practice}</h3>
                      <p className={cn(
                        "text-sm",
                        theme === 'light' ? "text-gray-600" : "text-gray-400"
                      )}>
                        {/* Show appropriate metrics based on record types */}
                        {hasHourlyOrFixed && (
                          <span>Total Hours: {calculateTotalHours(logs)}</span>
                        )}
                        {hasHourlyOrFixed && hasPercentage && (
                          <span> | </span>
                        )}
                        {hasPercentage && (
                          <span>Total Patients: {calculateTotalPatients(logs)}</span>
                        )}
                        <span> | Records: {logs.length}</span>
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                      {!showArchivedRecords && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className={cn(
                            "text-xs",
                            theme === 'light' 
                              ? "border-purple-300 bg-purple-50 text-purple-700" 
                              : "border-purple-500/30 bg-purple-900/20 text-purple-300"
                          )}
                          onClick={() => handleOpenInvoiceDialog(id, practice)}
                        >
                          <FileText className="h-3.5 w-3.5 mr-1" />
                          Generate Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Desktop table view */}
                  <div className="hidden md:block overflow-x-auto mt-3">
                    <table className="w-full text-sm">
                      <thead className={cn(
                        "border-b",
                        theme === 'light' 
                          ? "text-gray-700 border-gray-200" 
                          : "text-gray-400 border-purple-500/20"
                      )}>
                        <tr>
                          <th className="text-left py-2 px-2" style={{width: "25%"}}>Date / Type</th>
                          <th className="text-left py-2 px-2" style={{width: "60%"}}>Details</th>
                          <th className="text-right py-2 px-2" style={{width: "15%"}}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map(log => (
                          <tr key={log.id} className={cn(
                            "border-b",
                            theme === 'light'
                              ? "border-gray-200 hover:bg-gray-50" 
                              : "border-purple-500/10 hover:bg-[#2A2A3E]"
                          )}>
                            <td className={cn(
                              "py-2 px-2", 
                              theme === 'light' ? "text-gray-900" : "text-white"
                            )}>
                              <div className="flex justify-between items-center">
                                <div className="text-sm font-medium">{log.date ? safeFormatDate(log.date, "dd/MM/yyyy") : 'Unknown date'}</div>
                                <span className={cn(
                                  "text-[10px] px-2 py-0.5 rounded-full font-medium ml-2 border",
                                  theme === 'light'
                                    ? "border-purple-400 text-purple-700 bg-transparent"
                                    : "border-purple-400 text-purple-200 bg-transparent"
                                )}
                                style={{ background: 'transparent' }}
                                aria-label={`Rate type: ${log.rateType}`}
                              >
                                  {log.rateType}
                                </span>
                              </div>
                            </td>
                            <td className={cn(
                              "py-2 px-2",
                              theme === 'light' ? "text-gray-900" : "text-white"
                            )}>
                              <div className="flex flex-col">
                                <div className={theme === 'light' ? "text-gray-900" : "text-white"}>
                                  {getLogDescription(log)}
                                </div>
                                {log.description && (
                                  <div className={cn(
                                    "text-xs mt-1",
                                    theme === 'light' ? "text-gray-500" : "text-purple-300"
                                  )}>
                                    Note: {log.description}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-right">
                              <Button 
                                size="icon" 
                                variant="ghost"
                                className={cn(
                                  "h-8 w-8",
                                  theme === 'light'
                                    ? "text-red-600 hover:text-red-700 hover:bg-red-100"
                                    : "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                )}
                                onClick={() => handleDelete(log.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {showArchivedRecords && (
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className={cn(
                                    "h-8 w-8",
                                    theme === 'light'
                                      ? "text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                      : "text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                                  )}
                                  onClick={() => handleUnarchive(log)}
                                >
                                  <Undo className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile card view */}
                  <div className="md:hidden space-y-3 mt-3">
                    {logs.map(log => (
                      <div key={log.id} className={cn(
                        "p-3 rounded-lg border",
                        theme === 'light'
                          ? "bg-white border-gray-200"
                          : "bg-[#2A2A3E] border-purple-500/10"
                      )}>
                        <div className="flex justify-between items-center">
                          <div className={cn(
                            "font-medium text-sm",
                            theme === 'light' ? "text-gray-900" : "text-white"
                          )}>
                            {log.date ? safeFormatDate(log.date, "dd/MM/yyyy") : 'Unknown date'}
                          </div>
                          <div className="flex items-center">
                            <span className={cn(
                            "text-[10px] px-2 py-0.5 rounded-full font-medium ml-2 border",
                            theme === 'light'
                              ? "border-purple-400 text-purple-700 bg-transparent"
                              : "border-purple-400 text-purple-200 bg-transparent"
                          )}
                          style={{ background: 'transparent' }}
                          aria-label={`Rate type: ${log.rateType}`}
                        >
                            {log.rateType}
                          </span>
                            <div className="flex items-center gap-1">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className={cn(
                                  "h-7 w-7 p-0 -mt-0 -mr-1",
                                  theme === 'light' 
                                    ? "text-red-600 hover:text-red-700 hover:bg-red-100" 
                                    : "text-red-400 hover:text-red-300 hover:bg-red-900/20"
                                )}
                                onClick={() => handleDelete(log.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              {showArchivedRecords && (
                                <Button 
                                  size="icon" 
                                  variant="ghost"
                                  className={cn(
                                    "h-8 w-8",
                                    theme === 'light' 
                                      ? "text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                      : "text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
                                  )}
                                  onClick={() => handleUnarchive(log)}
                                >
                                  <Undo className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={cn(
                          "mt-2 text-sm",
                          theme === 'light' ? "text-gray-900" : "text-white"
                        )}>
                          {getLogDescription(log)}
                          {log.description && (
                            <div className={cn(
                              "mt-1 text-xs",
                              theme === 'light' ? "text-gray-600" : "text-gray-400"
                            )}>
                              Note: {log.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Invoice Dialog */}
      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={handleCloseInvoiceDialog}>
        <DialogContent className={cn(
          theme === 'light'
            ? 'bg-white border-gray-200 text-gray-900'
            : 'bg-[#2A2A3E] border-purple-500/20 text-white'
        )}>
          <>
            <DialogHeader>
              <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-white')}>Create Invoice</DialogTitle>
              <DialogDescription className={cn(theme === 'light' ? 'text-gray-500' : 'text-gray-300')}>
                {selectedPracticeForInvoice && `Create an invoice for ${selectedPracticeForInvoice.name}`}
              </DialogDescription>
            </DialogHeader>
            {selectedPracticeForInvoice && (
              <>
                <h4 className={cn('text-sm font-medium mb-2', theme === 'light' ? 'text-purple-700' : 'text-purple-300')}>Select records to include:</h4>
                <div className={cn('rounded-lg p-3 max-h-60 overflow-y-auto', theme === 'light' ? 'bg-gray-100' : 'bg-[#1F1F2E]')}>
                  {selectedLogs.map(log => (
                    <div key={log.id} className={cn('flex justify-between items-center py-2 border-b last:border-b-0', theme === 'light' ? 'border-gray-200' : 'border-purple-500/10')}>
                      <div className="flex flex-1 items-center">
                        <div className="mr-3">
                          <Switch 
                            checked={log.selected !== false}
                            onCheckedChange={() => toggleLogSelection(log.id)}
                            className="data-[state=checked]:bg-purple-600"
                          />
                        </div>
                        <div className="flex-1">
                          <p className={cn('text-sm', theme === 'light' ? 'text-gray-900' : 'text-white')}>{log.date ? safeFormatDate(log.date, "dd MMM yyyy") : 'Unknown date'}</p>
                          <div className={cn('flex flex-wrap items-center text-xs space-x-1', theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>
                            <span>{getLogDescription(log)}</span>
                          </div>
                        </div>
                      </div>
                      <span className={cn('font-medium ml-3', theme === 'light' ? 'text-gray-900' : 'text-white')}>
                        {formatCurrency(calculateLogTotal(log))}
                      </span>
                    </div>
                  ))}
                </div>
                <div className={cn('mt-4 p-3 rounded-lg', theme === 'light' ? 'bg-gray-100' : 'bg-[#1F1F2E]')}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={cn('text-sm font-medium', theme === 'light' ? 'text-gray-900' : 'text-white')}>Selected Records</h4>
                    <span className={cn('text-sm', theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>{selectedLogsForPdf.filter(log => log.selected !== false).length} records</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {selectedLogsForPdf
                      .filter(log => log.selected !== false)
                      .map(log => (
                        <div key={log.id} className={cn('flex justify-between py-1 border-b last:border-b-0 text-xs', theme === 'light' ? 'border-gray-200' : 'border-gray-800')}>
                          <span>{log.date ? safeFormatDate(log.date, 'dd MMM yyyy') : 'Unknown date'}</span>
                          <span>{formatCurrency(calculateLogTotal(log))}</span>
                        </div>
                      ))}
                  </div>
                  {/* Preview Button - below selected records, right-aligned */}
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      aria-label="Preview Invoice"
                      onClick={handlePreviewInvoice}
                      className={cn(
                        'flex items-center gap-1 border-0 bg-transparent outline-none shadow-none',
                        'text-gray-400 hover:text-[#a259ff] transition-colors',
                        'font-normal',
                        'py-2 px-2 sm:py-1.5 sm:px-1',
                        'rounded-md',
                        theme === 'light' ? 'hover:text-[#a259ff]' : 'hover:text-purple-300',
                        invoiceNumberLoading && 'opacity-60 cursor-not-allowed'
                      )}
                      style={{ cursor: invoiceNumberLoading ? 'not-allowed' : 'pointer', userSelect: 'none', minHeight: 0, minWidth: 0 }}
                      disabled={invoiceNumberLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-eye h-4 w-4">
                        <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      <span
                        className="hidden sm:inline align-middle"
                        style={{ fontSize: '0.85rem', fontWeight: 400, letterSpacing: 0.1, color: 'inherit' }}
                      >{invoiceNumberLoading ? 'Loading...' : 'Preview'}</span>
                    </button>
                  </div>
                  {/* Preview Icon Button - removed, now inline with Selected Records */}
                  <div className="flex flex-row gap-3 items-center">
                    <Button
                      size="default"
                      variant="default"
                      className={cn(
                        'flex-1 h-10 text-sm font-semibold flex items-center justify-center gap-2',
                        theme === 'light'
                          ? 'bg-purple-600 hover:bg-purple-700 text-white'
                          : 'bg-purple-700 hover:bg-purple-800 text-white',
                        pdfLoading && 'opacity-60 cursor-not-allowed'
                      )}
                    onClick={async () => {
                      await handleGeneratePDF();
                      // Reset preview invoice number after generating a real invoice
                      if (setPreviewInvoiceNumber) setPreviewInvoiceNumber(null);
                    }}
                    disabled={pdfLoading}
                    style={{ boxShadow: 'none' }}
                  >
                    <FileText className="h-5 w-5" />
                    <span>PDF</span>
                  </Button>
                    <Button
                      size="default"
                      variant="default"
                      className={cn(
                        'flex-1 h-10 text-sm font-semibold flex items-center justify-center gap-2',
                        theme === 'light'
                          ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          : 'bg-indigo-700 hover:bg-indigo-800 text-white',
                        (emailLoading || !generatedPdfFilename) && 'opacity-60 cursor-not-allowed'
                      )}
                      onClick={handleEmailSelf}
                      disabled={emailLoading || !generatedPdfFilename}
                      style={{ boxShadow: 'none' }}
                      title={!generatedPdfFilename ? 'Generate PDF first' : 'Email this invoice to yourself'}
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      <span>Email Self</span>
                    </Button>
                  </div>
                  {/*
                    Archiving logic:
                    Only records that are selected (toggled on) when you click Close will be archived.
                    Downloading PDF or Emailing does NOT archive or disable any records.
                  */}
                  <Button
                    size="default"
                    variant="default"
                    className={cn(
                      'w-full h-10 mt-6 text-sm font-semibold flex items-center justify-center gap-2',
                      theme === 'light'
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-purple-700 hover:bg-purple-800 text-white',
                      'sm:mt-6 mt-10' // more space above close button on mobile
                    )}
                    onClick={handleCloseInvoiceDialog}
                    style={{ boxShadow: 'none' }}
                  >
                    <span>&larr;</span>
                    Close
                  </Button>
                </div>
              </>
            )}
          </>
        </DialogContent>
      </Dialog>

      {/* Preview Modal using new component */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className={cn(
          theme === 'light'
            ? 'bg-white border-gray-200 text-gray-900'
            : 'bg-[#2A2A3E] border-purple-500/20 text-white',
          'max-w-2xl'
        )}>
          <DialogHeader>
            <DialogTitle className={cn(theme === 'light' ? 'text-black' : 'text-white')}>Invoice Preview</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto max-h-[70vh]">
            <DailyInvoicePreview
              logs={selectedLogsForPdf.filter(log => log.selected !== false)}
              profile={profileData}
              practice={
                (selectedPracticeForInvoice &&
                  (selectedPractices.find(p =>
                    p.prac_code === selectedPracticeForInvoice.id ||
                    p.practiceId === selectedPracticeForInvoice.id
                  )
                )) || selectedPracticeForInvoice || {}
              }
              rateType={selectedLogsForPdf.filter(log => log.selected !== false)[0]?.rateType || rateType}
              rateValue={selectedLogsForPdf.filter(log => log.selected !== false)[0]?.rateValue || rateValue}
              invoiceNumber={getPreviewInvoiceNumber ? getPreviewInvoiceNumber() : 'PREVIEW'}
              total={selectedLogsForPdf.filter(log => log.selected !== false).reduce((sum, log) => sum + calculateLogTotal(log), 0)}
            />
          </div>
          <DialogFooter className="pt-4 border-t mt-4">
            <Button onClick={() => setShowPreviewModal(false)} variant="outline" className="bg-purple-600 text-white hover:bg-purple-700 border-purple-600">Close Preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>













      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="bg-[#2A2A3E] border-purple-500/20 text-white">
          <DialogHeader>
            <DialogTitle>Email Invoice</DialogTitle>
            <DialogDescription>
              Use the form below to send the invoice via email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="emailTo" className="text-sm text-gray-300">To</Label>
              <Input
                id="emailTo"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="bg-[#1F1F2E] border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label htmlFor="emailSubject" className="text-sm text-gray-300">Subject</Label>
              <Input
                id="emailSubject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="bg-[#1F1F2E] border-gray-700 text-white mt-1"
              />
            </div>
            <div>
              <Label htmlFor="emailMessage" className="text-sm text-gray-300">Message</Label>
              <textarea
                id="emailMessage"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={6}
                className="w-full p-2 bg-[#1F1F2E] border border-gray-700 rounded-md text-white mt-1 resize-none"
              />
            </div>
            <div className="bg-[#1F1F2E] p-3 rounded-lg">
              <div className="flex items-center mb-2">
                <AlertCircle className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <h4 className="text-sm font-medium text-white">Selected Records</h4>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {selectedLogsForPdf.map(log => (
                  <div key={log.id} className="flex justify-between py-1 border-b border-gray-800 last:border-b-0 text-xs">
                    <span>{log.date ? safeFormatDate(log.date, 'dd MMM yyyy') : 'Unknown date'}</span>
                    <span>{formatCurrency(calculateLogTotal(log))}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-between font-medium">
                <span>Total:</span>
                <span>{formatCurrency(
                  selectedLogsForPdf.reduce((sum, log) => sum + calculateLogTotal(log), 0)
                )}</span>
              </div>
            </div>
            <div className="bg-[#1F1F2E] p-3 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-gray-300">
                  This is a template to help you craft your email. To send this invoice, download the PDF and send it from your email client.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowEmailDialog(false)}
              className="bg-transparent border-gray-600 text-gray-300"
            >
              Close
            </Button>
            <Button 
              variant="default"
              onClick={handleEmailSelf}
              disabled={emailLoading}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              title="Email this invoice to yourself"
            >
              {emailLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Emailing...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Email Self
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#2A2A3E] border-red-500/20 text-white">
          <DialogHeader>
            <DialogTitle>Delete Record</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this record? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {logToDelete && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400 text-xs">Date:</span>
                <span className="text-white block">{logToDelete.formattedDate}</span>
              </div>
              <div>
                <span className="text-gray-400 text-xs">Practice:</span>
                <span className="text-white block">{logToDelete.practiceName}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-400 text-xs">Amount:</span>
                <span className="text-white block">{formatCurrency(calculateLogTotal(logToDelete))}</span>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="bg-transparent border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              variant="default"
              onClick={handleDeleteLog}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unarchive Confirmation Dialog */}
      <Dialog open={showUnarchiveDialog} onOpenChange={setShowUnarchiveDialog}>
        <DialogContent className="bg-[#2A2A3E] border-blue-500/20 text-white">
          <DialogHeader>
            <DialogTitle>Restore Record</DialogTitle>
            <DialogDescription>
              Are you sure you want to restore this record to active records?
            </DialogDescription>
          </DialogHeader>
          {logToUnarchive && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-400 text-xs">Date:</span>
                <span className="text-white block">{logToUnarchive.formattedDate}</span>
              </div>
              <div>
                <span className="text-gray-400 text-xs">Practice:</span>
                <span className="text-white block">{logToUnarchive.practiceName}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-400 text-xs">Amount:</span>
                <span className="text-white block">{formatCurrency(calculateLogTotal(logToUnarchive))}</span>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline"
              onClick={() => setShowUnarchiveDialog(false)}
              className="bg-transparent border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button 
              variant="default"
              onClick={confirmUnarchive}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Practice Settings Dialog */}
      <Dialog open={showPracticeSettings} onOpenChange={setShowPracticeSettings}>
        <DialogContent className="bg-[#2A2A3E] border-purple-500/20 shadow-lg max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium text-white">Manage Practices</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <PracticeSettings
              user={user}
              onSave={handleSavePractices}
              onClose={() => setShowPracticeSettings(false)}
              initialSelectedPractices={selectedPractices}
              forceRefresh={true}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal for PDF/Email actions */}
      <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
        <DialogContent className="bg-white dark:bg-[#2A2A3E] text-gray-900 dark:text-gray-200 shadow-lg rounded-lg">
          <DialogHeader>
            <DialogTitle>{confirmationModalContent?.title}</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm leading-relaxed">
            {/* Custom message for PDF download confirmation */}
            {confirmationModalContent?.title === 'Invoice PDF Generated' ? (
              <>
                Your invoice PDF has been <b>downloaded</b> and can be found in your <b>Downloads</b> folder.
              </>
            ) : (
              confirmationModalContent?.description
            )}
          </div>
          <DialogFooter className="pt-4 border-t mt-4">
            <Button onClick={() => setShowConfirmationModal(false)} variant="outline" className="bg-purple-600 text-white hover:bg-purple-700 border-purple-600">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyRecordLog;